import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';
import EmojiPicker, { Theme as EmojiTheme } from 'emoji-picker-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiPlus, BiSortAlt2 } from 'react-icons/bi';
import { FiChevronDown, FiEdit2, FiFileText, FiRepeat, FiTrash2, FiX } from 'react-icons/fi';
import { MdDragIndicator } from 'react-icons/md';
import OutsideClickHandler from 'react-outside-click-handler';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { toast } from 'sonner';
import useDarkMode from 'use-dark-mode';
import { deleteCatalogItem, getCatalog, updateCatalogItem } from '../../api/catalog.service';
import {
    createCategory,
    deleteCategory,
    getCategoryById,
    reorderCategories as reorderCategoriesApi,
    updateCategory,
} from '../../api/category.service';
import { getDeckCSV, getDeckImages } from '../../api/decks.service';
import { getTextImages } from '../../api/texts.service';
import { clearCatalogNeedsRefresh, clearCatalogState, removeCatalogItem, setCatalogScroll, setCatalogSnapshot } from '../../app/catalog/catalog.slice';
import { fetchCategory, reorderCategories, removeCategoryOrSubcategory, updateCategoryOrSubcategoryInPlace } from '../../app/category/category.slice';
import { fetchDecks } from '../../app/decks/decks.slice';
import Actions from '../../components/Actions';
import Card from '../../components/Card';
import CatalogStatsModal from '../../components/CatalogStatsModal';
import DropdownSearch from '../../components/DropdownSearch';
import Modal from '../../components/Modal';
import SearchInput from '../../components/SearchInput';
import TextInput from '../../components/TextInput';
import { DEFAULT_DECK_AVATAR, getPlaceholderImageUrl } from '../../constants/images';
import styles from './Catalog.module.sass';
import { SORT_OPTIONS, useCatalogFilters } from './useCatalogFilters';

const CatalogPointerSensor = class extends PointerSensor {};
CatalogPointerSensor.activators = [{
    eventName: 'onPointerDown',
    handler: ({ nativeEvent: event }, { onActivation }) => {
        if (event.shiftKey || event.ctrlKey || event.metaKey) return false;
        if (!event.isPrimary || event.button !== 0) return false;
        onActivation?.({ event });
        return true;
    },
}];

const LEVELS_OPTIONS = ['Newbie', 'Beginner', 'Intermediate', 'Advanced', 'Master'];
const CATEGORIES_PER_PAGE = 5;

const LEVEL_BADGE_CLASS = {
  Newbie: styles.levelBadgeNewbie,
  Beginner: styles.levelBadgeBeginner,
  Intermediate: styles.levelBadgeIntermediate,
  Advanced: styles.levelBadgeAdvanced,
  Master: styles.levelBadgeMaster,
};

/** Exclude "My decks" from catalog category list (backoffice does not show it). */
function isMyDecksCategory(group) {
  const title = (group?.categoryTitle || '').trim().toLowerCase();
  return title === 'my decks';
}

/** For role ADMIN: exclude Academy and My texts from catalog. */
function shouldExcludeCategoryForAdmin(group, userRole) {
  if (userRole !== 'ADMIN') return false;
  const title = (group?.categoryTitle || '').trim().toLowerCase();
  return title === 'academy' || title === 'my texts';
}

/** True when the category has at least one item in any subcategory. */
function categoryHasItems(cat) {
  return (cat.subcategories || []).some((s) => (s.items || []).length > 0);
}

/** Get stable item id (sourceId or from catalogRef like "deck:xxx"). */
function getItemId(item) {
  const id = item?.sourceId ?? item?.catalogRef?.split(':')?.[1] ?? item?._id;
  return id != null ? String(id) : '';
}

/**
 * Deduplicate catalog items globally: each sourceId appears exactly once.
 * Backend aggregation may return the same document multiple times (same _id, same
 * sourceId) inside one subcategory, or across subcategories after a move.
 */
function deduplicateCatalogItems(categories) {
  if (!categories?.length) return categories;
  const seen = new Set();
  return categories.map((cat) => ({
    ...cat,
    subcategories: (cat.subcategories || []).map((sub) => ({
      ...sub,
      items: (sub.items || []).filter((item) => {
        const id = getItemId(item);
        if (!id) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      }),
    })),
  }));
}

/** Find the scrollable parent (Page's .container has overflow: auto), not window. */
function getScrollContainer(fromElement) {
  let el = fromElement;
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY || style.overflow;
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

const defaultTranslations = () =>
  ['en', 'es', 'fr', 'ru', 'pt'].map((lang) => ({ lang, trans: '' }));

/** Get full category (with translations) from category API by id. */
function getFullCategoryById(categoriesList, categoryId) {
  if (!categoriesList?.length || !categoryId) return null;
  const id = String(categoryId);
  return categoriesList.find((c) => String(c._id) === id) || null;
}

/** Extract categoryId from dnd-kit over.id (may be "category:xyz" or just "xyz"). */
function getCategoryIdFromOverId(overId) {
  if (overId == null) return null;
  const s = String(overId);
  return s.startsWith('category:') ? s.slice(9) : s;
}

/** Get full subcategory (with translations) from category API by parent and sub id. */
function getFullSubcategoryById(categoriesList, categoryId, subCategoryId) {
  const cat = getFullCategoryById(categoriesList, categoryId);
  if (!cat?.subCategories?.length || !subCategoryId) return null;
  const subId = String(subCategoryId);
  const sub = cat.subCategories.find((s) => String(s._id) === subId);
  return sub ? { ...sub, parent: cat._id || categoryId } : null;
}

/**
 * Resolve the display title from translations: user language first, English as fallback.
 * The API serializes a top-level category's `title` from translations, but NOT a
 * subcategory's — so a subcategory's `title` field keeps its stale value after its
 * translations are edited. Deriving the title here keeps the displayed name in sync.
 */
function getTranslatedTitle(translations, lang) {
  if (!Array.isArray(translations) || translations.length === 0) return null;
  const byLang = translations.find((tr) => tr?.lang === lang)?.trans;
  if (byLang) return byLang;
  return translations.find((tr) => tr?.lang === 'en')?.trans || null;
}

function normalizeEditableCategoryTarget(entity, fallback) {
  if (!entity) return fallback;
  const parent = entity?.parent?._id || entity?.parent || fallback?.parent || null;
  return {
    ...fallback,
    ...entity,
    _id: entity?._id || fallback?._id,
    title:
      entity?.translations?.find((t) => t?.lang === 'en')?.trans ||
      entity?.title ||
      fallback?.title ||
      '',
    emoji: entity?.emoji ?? fallback?.emoji ?? '',
    translations: entity?.translations || fallback?.translations || [],
    parent,
  };
}

/** Get items from groups that match the selected IDs set. */
function getSelectedItemsFromGroups(catalogCategoryGroups, selectedIds) {
  const items = [];
  for (const cat of catalogCategoryGroups || []) {
    for (const sub of cat.subcategories || []) {
      for (const item of sub.items || []) {
        if (selectedIds.has(getItemId(item))) items.push(item);
      }
    }
  }
  return items;
}

/** Get display title from catalog item (string, i18n object, or titleTranslations). */
function getItemDisplayTitle(item) {
  if (!item) return '';
  const title = item.title;
  if (typeof title === 'string' && title.trim()) return title;
  if (title && typeof title === 'object') return title.es || title.en || Object.values(title).find(Boolean) || '';
  const trans = item.titleTranslations;
  if (Array.isArray(trans) && trans.length > 0) {
    const t = trans.find((x) => x?.trans?.trim()) || trans[0];
    return t?.trans?.trim() || '';
  }
  if (item.key && typeof item.key === 'string') return item.key;
  if (item.catalogRef) return item.catalogRef;
  return '';
}

export default function Catalog({ isActive = true }) {
  const darkMode = useDarkMode(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { decks, category, catalog: catalogState, user } = useSelector((state) => state);
  const categoriesFromApi = category?.value ?? [];
  const userLang = user?.value?.lang || 'en';

  const {
    filters,
    setFilters,
    resetFilters,
    catalogParams,
  } = useCatalogFilters();

  const [searchInput, setSearchInput] = useState(filters.title);
  const searchDebounceRef = useRef(null);

  // Categories loaded 5 at a time; each page already includes subcategories + items.
  const [catalogCategoryGroups, setCatalogCategoryGroups] = useState([]);
  const [loadingCategoryTree, setLoadingCategoryTree] = useState(false);
  const [loadingMoreCategories, setLoadingMoreCategories] = useState(false);
  const [catalogTotalPages, setCatalogTotalPages] = useState(0);
  const [catalogCurrentPage, setCatalogCurrentPage] = useState(-1);
  // refs so IntersectionObserver callback always sees current values
  const catalogCurrentPageRef = useRef(-1);
  const catalogTotalPagesRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);
  const skipNextLoadRef = useRef(false);
  const isFirstMountRef = useRef(true);
  const catalogRootRef = useRef(null);
  const restoredScrollRef = useRef(null);
  const weJustRestoredRef = useRef(false);
  const moveInProgressRef = useRef(false);

  const [expandedCategoryIds, setExpandedCategoryIds] = useState(() => new Set());
  const [expandedSubcategoryIds, setExpandedSubcategoryIds] = useState(() => new Set());

  const [newContentItems, setNewContentItems] = useState([]);
  const [categoryIdSelected, setCategoryIdSelected] = useState(null);
  const [isVisibleModalEdit, setIsVisibleModalEdit] = useState(false);
  const [isVisibleModalNewCategory, setIsVisibleModalNewCategory] = useState(false);
  const [emojiPickerTarget, setEmojiPickerTarget] = useState(null);
  const [isVisibleModalNewSubcategory, setIsVisibleModalNewSubcategory] = useState(false);
  const [isVisibleModalAddDeck, setIsVisibleModalAddDeck] = useState(false);
  const [isVisibleModalDeleteDeck, setIsVisibleModalDeleteDeck] = useState(false);
  const [deckSelected, setDeckSelected] = useState(null);
  const [newCategoryForm, setNewCategoryForm] = useState({
    title: '',
    emoji: '',
    translations: defaultTranslations(),
  });
  const [newSubcategoryForm, setNewSubcategoryForm] = useState({
    title: '',
    emoji: '',
    translations: defaultTranslations(),
    parent: null,
  });
  const [subcategoryMovedLabel, setSubcategoryMovedLabel] = useState({});
  const [activeDragItem, setActiveDragItem] = useState(null);
  const [activeDragSubcategory, setActiveDragSubcategory] = useState(null);
  const [activeDragCategory, setActiveDragCategory] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());
  const [activeDragItems, setActiveDragItems] = useState([]);
  const lastSelectedFlatIndexRef = useRef(-1);
  const categoryDragSnapshotRef = useRef(null);

  // Per-subcategory pagination: { [catId:subId]: { nextPage, hasMore, loading } }
  const [subcategoryPagination, setSubcategoryPagination] = useState({});
  const subcategoryPaginationRef = useRef({});
  useEffect(() => {
    subcategoryPaginationRef.current = subcategoryPagination;
  }, [subcategoryPagination]);

  const [statsModalState, setStatsModalState] = useState({ visible: false, item: null });
  const [deleteModalState, setDeleteModalState] = useState({ visible: false, item: null });
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [isDeletingItem, setIsDeletingItem] = useState(false);

  // Delete category/subcategory modal
  const [deleteCatModalState, setDeleteCatModalState] = useState({ visible: false, target: null });
  const [isDeletingCat, setIsDeletingCat] = useState(false);

  useEffect(() => {
    if (deleteModalState.visible) setDeleteConfirmInput('');
  }, [deleteModalState.visible]);

  const editTarget = categoryIdSelected;

  /** Save current scroll position to Redux and navigate. Use before any navigation so position is preserved when returning. */
  const saveScrollAndNavigate = useCallback((path, state) => {
    const container = catalogRootRef.current ? getScrollContainer(catalogRootRef.current) : null;
    const position = container ? container.scrollTop : window.scrollY;
    dispatch(setCatalogScroll(position));
    history.push(path, state);
  }, [dispatch, history]);

  /**
   * Load a page of categories (CATEGORIES_PER_PAGE), each with all subcategories and items.
   * reset=true → first page, replaces list and auto-expands loaded categories.
   * reset=false → next page, appends to list (triggered by scroll sentinel).
   */
  const loadCategoriesFromCatalog = useCallback(async (reset = true) => {
    const nextPage = reset ? 0 : catalogCurrentPageRef.current + 1;
    if (!reset && (loadingMoreRef.current || nextPage >= catalogTotalPagesRef.current)) return;

    if (reset) {
      setLoadingCategoryTree(true);
      setSubcategoryPagination({});
      subcategoryPaginationRef.current = {};
    } else {
      setLoadingMoreCategories(true);
      loadingMoreRef.current = true;
    }

    try {
      const res = await getCatalog({
        groupedBy: 'category',
        take: CATEGORIES_PER_PAGE,
        page: nextPage,
        order: catalogParams.order || 'name',
        available: catalogParams.available,
        itemsPerCategory: 50,
        type: catalogParams.type,
        title: catalogParams.title,
        level: catalogParams.level,
        premium: catalogParams.premium,
        includeEmptyCategories: true,
      });
      // getApi returns error object on failure instead of throwing - detect and show feedback
      if (res?.response?.status >= 400) {
        const msg = res?.response?.data?.message || res?.message || `Error ${res?.response?.status}`;
        toast.error(t('catalog.loadError', { message: msg }) || `Error loading catalog: ${msg}`);
        if (reset) setCatalogCategoryGroups([]);
        return;
      }
      const raw = res?.data?.categories || [];
      const filtered = raw.filter(
        (g) => !isMyDecksCategory(g) && !shouldExcludeCategoryForAdmin(g, user?.value?.role)
      );
      const incoming = deduplicateCatalogItems(filtered);
      const pagination = res?.data?.pagination || {};
      const totalPages = pagination.pages ?? 0;
      const initialSubcategoryPagination = {};
      incoming.forEach((cat) => {
        (cat.subcategories || []).forEach((sub) => {
          const subId = sub.subCategoryId || '__limbo__';
          if (!sub.subCategoryId) return;
          const loaded = (sub.items || []).length;
          const total = Number.isFinite(sub.totalItems) ? sub.totalItems : undefined;
          initialSubcategoryPagination[`${cat.categoryId}:${subId}`] = {
            nextPage: Math.max(1, Math.ceil(loaded / ITEMS_PER_SUBCATEGORY_PAGE)),
            hasMore: typeof total === 'number' ? loaded < total : loaded >= ITEMS_PER_SUBCATEGORY_PAGE,
            loading: false,
          };
        });
      });

      setCatalogTotalPages(totalPages);
      setCatalogCurrentPage(nextPage);
      catalogCurrentPageRef.current = nextPage;
      catalogTotalPagesRef.current = totalPages;
      setSubcategoryPagination((prev) => (reset ? initialSubcategoryPagination : { ...prev, ...initialSubcategoryPagination }));
      subcategoryPaginationRef.current = reset ? initialSubcategoryPagination : { ...subcategoryPaginationRef.current, ...initialSubcategoryPagination };

      /** A category is worth auto-expanding only when it has at least one item. */
      const hasItems = (cat) =>
        (cat.subcategories || []).some((s) => (s.items || []).length > 0);

      if (reset) {
        setCatalogCategoryGroups(incoming);
        // First page: expand all categories that have items
        setExpandedCategoryIds(
          new Set(incoming.filter(hasItems).map((c) => c.categoryId))
        );
        // Expand all subcategories that have items
        const subKeys = new Set();
        incoming.forEach((cat) => {
          (cat.subcategories || []).forEach((sub) => {
            if ((sub.items || []).length > 0) {
              subKeys.add(`${cat.categoryId}:${sub.subCategoryId || '__limbo__'}`);
            }
          });
        });
        setExpandedSubcategoryIds(subKeys);
      } else {
        setCatalogCategoryGroups((prev) => [...prev, ...incoming]);
        // Subsequent pages: expand only the first 2 categories that have items
        const toExpand = incoming.filter(hasItems).slice(0, 2);
        setExpandedCategoryIds((prev) => {
          const next = new Set(prev);
          toExpand.forEach((c) => next.add(c.categoryId));
          return next;
        });
        // Expand subcategories that have items in newly loaded categories
        setExpandedSubcategoryIds((prev) => {
          const next = new Set(prev);
          incoming.forEach((cat) => {
            (cat.subcategories || []).forEach((sub) => {
              if ((sub.items || []).length > 0) {
                next.add(`${cat.categoryId}:${sub.subCategoryId || '__limbo__'}`);
              }
            });
          });
          return next;
        });
      }
    } catch (e) {
      console.error('Error loading catalog:', e);
    } finally {
      setLoadingCategoryTree(false);
      setLoadingMoreCategories(false);
      loadingMoreRef.current = false;
    }
  }, [catalogParams, t, user?.value?.role]);

  const handleDeleteConfirm = async () => {
    if (!deleteModalState.item) return;
    const item = deleteModalState.item;
    // Usar sourceId (ID real del deck/text). NO item._id (es el _id del documento catalog).
    const sourceId = String(item.sourceId ?? item.catalogRef?.split(':')?.[1] ?? '');
    const itemType = String(item.type ?? item.catalogRef?.split(':')?.[0] ?? '');
    if (!sourceId || !itemType) {
      toast.error(t('catalog.itemDeleteFailed') || 'Failed to delete item');
      return;
    }
    setIsDeletingItem(true);
    try {
      const response = await deleteCatalogItem({
        _id: sourceId,
        type: itemType,
      });
      if (!response || response.status < 200 || response.status >= 300) {
        throw new Error('Catalog delete request failed');
      }
      toast.success(t('catalog.itemDeletedSuccess') || 'Item deleted successfully');
      setDeleteModalState({ visible: false, item: null });
      // Remove from local state (no API reload). Redux snapshot updated by persist effect.
      setCatalogCategoryGroups((prev) =>
        prev.map((cat) => ({
          ...cat,
          subcategories: (cat.subcategories || []).map((sub) => ({
            ...sub,
            items: (sub.items || []).filter(
              (i) => String(i?.sourceId ?? i?.catalogRef?.split(':')?.[1] ?? i?._id ?? '') !== sourceId
            ),
          })),
        }))
      );
      setNewContentItems((prev) =>
        prev.filter((i) => String(i?.sourceId ?? i?.catalogRef?.split(':')?.[1] ?? i?._id ?? '') !== sourceId)
      );
      dispatch(removeCatalogItem({ sourceId, type: itemType }));
    } catch (error) {
      console.error(error);
      toast.error(t('catalog.itemDeleteFailed') || 'Failed to delete item');
    } finally {
      setIsDeletingItem(false);
    }
  };

  const loadNewContent = useCallback(async () => {
    try {
      const res = await getCatalog({
        newContent: true,
        type: catalogParams.type,
        title: catalogParams.title,
        level: catalogParams.level,
        premium: catalogParams.premium,
        available: catalogParams.available,
        order: catalogParams.order,
        take: 20,
        page: 0,
      });
      if (res?.response?.status >= 400) return;
      const data = res?.data;
      const items = Array.isArray(data?.results) ? data.results : [];
      setNewContentItems(items);
    } catch (e) {
      console.error(e);
    }
  }, [catalogParams.type, catalogParams.title, catalogParams.level, catalogParams.premium, catalogParams.available, catalogParams.order]);

  const ITEMS_PER_SUBCATEGORY_PAGE = 50;

  /** Load next 50 items for a subcategory. API: categoryId + subCategoryId → flat search with page/take. */
  const loadMoreSubcategoryItems = useCallback(
    async (categoryId, subCategoryId) => {
      if (!categoryId || !subCategoryId || subCategoryId === '__limbo__' || subCategoryId === 'limbo') return;
      const key = `${categoryId}:${subCategoryId}`;
      const current = subcategoryPaginationRef.current[key];
      const nextPage = current?.nextPage ?? 1;
      if (current?.loading || (current && !current.hasMore)) return;

      setSubcategoryPagination((prev) => ({ ...prev, [key]: { ...prev[key], loading: true } }));

      try {
        const res = await getCatalog({
          categoryId,
          subCategoryId,
          take: ITEMS_PER_SUBCATEGORY_PAGE,
          page: nextPage,
          order: catalogParams.order || 'name',
          available: catalogParams.available,
          type: catalogParams.type,
          title: catalogParams.title,
          level: catalogParams.level,
          premium: catalogParams.premium,
        });

        if (res?.response?.status >= 400) {
          toast.error(t('catalog.loadError', { message: res?.response?.data?.message }) || 'Error loading items');
          setSubcategoryPagination((prev) => ({ ...prev, [key]: { ...prev[key], loading: false } }));
          return;
        }

        const results = res?.data?.results ?? [];
        const pagination = res?.data?.pagination ?? {};
        const pages = Number.isFinite(pagination.pages) ? pagination.pages : 1;
        const page =
          (Number.isFinite(pagination.currentPage) ? pagination.currentPage - 1 : undefined) ??
          (Number.isFinite(pagination.page) ? pagination.page : nextPage);
        const currentGroup = catalogCategoryGroups
          .find((cat) => String(cat.categoryId) === String(categoryId))
          ?.subcategories?.find((sub) => String(sub.subCategoryId ?? sub._id) === String(subCategoryId));
        const existingItems = currentGroup?.items || [];
        const seenExisting = new Set(existingItems.map(getItemId).filter(Boolean));
        const uniqueResults = results.filter((item) => {
          const id = getItemId(item);
          if (!id || seenExisting.has(id)) return false;
          seenExisting.add(id);
          return true;
        });

        setCatalogCategoryGroups((prev) =>
          prev.map((cat) => {
            if (String(cat.categoryId) !== String(categoryId)) return cat;
            return {
              ...cat,
              subcategories: (cat.subcategories || []).map((sub) => {
                const subId = sub.subCategoryId ?? sub._id;
                if (String(subId) !== String(subCategoryId)) return sub;
                const existing = sub.items || [];
                const seen = new Set(existing.map(getItemId).filter(Boolean));
                const appended = results.filter((item) => {
                  const id = getItemId(item);
                  if (!id || seen.has(id)) return false;
                  seen.add(id);
                  return true;
                });
                return { ...sub, items: [...existing, ...appended] };
              }),
            };
          })
        );

        const totalFromInitialGroup = catalogCategoryGroups
          .find((cat) => String(cat.categoryId) === String(categoryId))
          ?.subcategories?.find((sub) => String(sub.subCategoryId ?? sub._id) === String(subCategoryId))
          ?.totalItems;
        const loadedCount = existingItems.length + uniqueResults.length;
        const backendExhausted = results.length === 0 || pages <= 0 || page >= pages - 1;
        const hasMore = backendExhausted
          ? false
          : Number.isFinite(totalFromInitialGroup)
            ? loadedCount < totalFromInitialGroup
            : page < pages - 1;

        setSubcategoryPagination((prev) => ({
          ...prev,
          [key]: {
            nextPage: Math.max(nextPage + 1, page + 1),
            hasMore,
            loading: false,
          },
        }));
      } catch (e) {
        console.error('Error loading more subcategory items:', e);
        toast.error(t('catalog.loadError', { message: e?.message }) || 'Error loading items');
        setSubcategoryPagination((prev) => ({ ...prev, [key]: { ...prev[key], loading: false } }));
      }
    },
    [catalogCategoryGroups, catalogParams, t]
  );

  // Load full categories (with translations) from /admin/category for edit modal
  useEffect(() => {
    dispatch(fetchCategory());
  }, [dispatch]);

  const filtersKey = location.search || '';
  const prevPathnameRef = useRef(location.pathname);
  const restoringUrlRef = useRef(false);

  // Restore catalog state from Redux when returning to the page with same filters.
  // Only restore URL when navigating TO /catalog from a different path (e.g. sidebar).
  // Do NOT restore when user is already on /catalog and clicked "All" (empty filters).
  // Also ignore URL changes if the catalog is hidden in the background (KeepAlive).
  // When catalogNeedsRefresh (e.g. after adding deck/text from non-catalog path), force refetch instead of restore.
  useEffect(() => {
    if (!isActive) return;

    const prevPath = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    const stored = catalogState;
    const hasStoredData = stored?.filtersKey !== undefined && Array.isArray(stored.categoryGroups) && stored.categoryGroups.length > 0;
    const cameFromDifferentPath = prevPath !== '/catalog' && location.pathname === '/catalog';

    if (cameFromDifferentPath) {
      restoringUrlRef.current = false; // Reset just in case it got stuck
    }

    if (stored?.catalogNeedsRefresh && cameFromDifferentPath) {
      dispatch(clearCatalogNeedsRefresh());
      dispatch(clearCatalogState());
      return;
    }

    if (hasStoredData && !filtersKey && stored.filtersKey && cameFromDifferentPath) {
      // User navigated to /catalog from another page (e.g. sidebar) - restore their previous URL with filters
      restoringUrlRef.current = true;
      const search = stored.filtersKey.startsWith('?') ? stored.filtersKey : `?${stored.filtersKey}`;
      history.replace({ pathname: '/catalog', search });
      return;
    }

    if (stored?.filtersKey === filtersKey && Array.isArray(stored.categoryGroups) && stored.categoryGroups.length > 0) {
      restoringUrlRef.current = false; // Successfully restored
      setCatalogCategoryGroups(stored.categoryGroups);
      setCatalogTotalPages(stored.totalPages ?? 0);
      setCatalogCurrentPage(stored.currentPage ?? -1);
      catalogCurrentPageRef.current = stored.currentPage ?? -1;
      catalogTotalPagesRef.current = stored.totalPages ?? 0;
      setExpandedCategoryIds(new Set(stored.expandedCategoryIds || []));
      setExpandedSubcategoryIds(new Set(stored.expandedSubcategoryIds || []));
      setNewContentItems(stored.newContentItems || []);
      skipNextLoadRef.current = true;
      restoredScrollRef.current = stored.scrollPosition ?? 0;
      weJustRestoredRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, location.pathname, location.search, isActive]);

  // When filters change (URL search), clear persisted state and scroll to top.
  // Skip when we're restoring from stored state (filtersKey matches stored - user navigated back, not changed filters).
  useEffect(() => {
    if (!isActive) return; // Prevent hidden component from reacting to other pages' URLs changing

    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
      return;
    }
    
    if (restoringUrlRef.current) {
      return; // Mid-restore, don't clear state!
    }

    if (catalogState?.filtersKey === filtersKey && catalogState?.categoryGroups?.length > 0) {
      return; // Restoring from stored state, don't clear
    }
    dispatch(clearCatalogState());
    const container = catalogRootRef.current ? getScrollContainer(catalogRootRef.current) : null;
    if (container) container.scrollTop = 0;
    else window.scrollTo(0, 0);
  }, [filtersKey, dispatch, catalogState?.filtersKey, catalogState?.categoryGroups?.length, isActive]);

  // Apply restored scroll position synchronously after DOM update (before paint).
  // Only apply when the current position differs significantly (avoids micro-jumps when KeepAlive
  // already preserved the DOM scroll). Do NOT re-apply after a delay — that causes a visible
  // "pequeño scroll" when images/layout change and the same scrollTop no longer matches the view.
  useLayoutEffect(() => {
    if (!weJustRestoredRef.current || restoredScrollRef.current == null || catalogCategoryGroups.length === 0) return;
    const scrollTop = restoredScrollRef.current;
    weJustRestoredRef.current = false;
    restoredScrollRef.current = null;
    const container = catalogRootRef.current ? getScrollContainer(catalogRootRef.current) : null;
    const currentScroll = container ? container.scrollTop : window.scrollY;
    // Only restore if position differs by more than 2px — avoids forcing a value when DOM already correct
    if (Math.abs(currentScroll - scrollTop) > 2) {
      if (container) container.scrollTop = scrollTop;
      else window.scrollTo(0, scrollTop);
    }
  }, [catalogCategoryGroups]);

  // Keep scroll position in Redux on scroll (throttled) so it's correct when leaving via back button or unmount
  useEffect(() => {
    const root = catalogRootRef.current;
    if (!root) return undefined;
    const container = getScrollContainer(root);
    const el = container || window;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const position = container ? container.scrollTop : window.scrollY;
        dispatch(setCatalogScroll(position));
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [dispatch]);

  // Fallback: save scroll on unmount in case ref is still attached
  useEffect(() => {
    const root = catalogRootRef.current;
    return () => {
      const container = root ? getScrollContainer(root) : null;
      const position = container ? container.scrollTop : window.scrollY;
      dispatch(setCatalogScroll(position));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isActive) return;
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }
    loadCategoriesFromCatalog();
  }, [loadCategoriesFromCatalog, isActive]);

  useEffect(() => {
    if (!isActive) return;
    loadNewContent();
  }, [loadNewContent, isActive]);

  // Persist catalog state to Redux whenever data or pagination changes (so it can be restored on return).
  // When we just restored from Redux, use the stored scroll position (don't read DOM yet or we'd persist 0).
  useEffect(() => {
    if (!isActive) return;
    const hasData = catalogCategoryGroups.length > 0 || catalogCurrentPage >= 0 || newContentItems.length > 0;
    if (!hasData) return;
    const container = catalogRootRef.current ? getScrollContainer(catalogRootRef.current) : null;
    const scrollPosition = weJustRestoredRef.current && restoredScrollRef.current != null
      ? restoredScrollRef.current
      : (container ? container.scrollTop : window.scrollY);
    dispatch(
      setCatalogSnapshot({
        filtersKey,
        categoryGroups: catalogCategoryGroups,
        totalPages: catalogTotalPages,
        currentPage: catalogCurrentPage,
        expandedCategoryIds: Array.from(expandedCategoryIds),
        expandedSubcategoryIds: Array.from(expandedSubcategoryIds),
        newContentItems,
        scrollPosition,
      })
    );
  }, [filtersKey, catalogCategoryGroups, catalogTotalPages, catalogCurrentPage, expandedCategoryIds, expandedSubcategoryIds, newContentItems, dispatch, isActive]);

  // Sync pagination refs so the IntersectionObserver always reads current values
  useEffect(() => {
    catalogCurrentPageRef.current = catalogCurrentPage;
    catalogTotalPagesRef.current = catalogTotalPages;
  }, [catalogCurrentPage, catalogTotalPages]);

  // IntersectionObserver: when sentinel enters viewport, load next 5 categories.
  // Re-runs whenever the sentinel mounts/unmounts (catalogCurrentPage/catalogTotalPages change).
  useEffect(() => {
    if (!isActive) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        if (loadingMoreRef.current) return;
        const page = catalogCurrentPageRef.current;
        const pages = catalogTotalPagesRef.current;
        if (page < 0 || page + 1 >= pages) return;
        loadCategoriesFromCatalog(false);
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  // catalogCurrentPage / catalogTotalPages trigger re-attach once the sentinel div renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCategoriesFromCatalog, catalogCurrentPage, catalogTotalPages, isActive]);


  useEffect(() => {
    setSearchInput(filters.title);
  }, [filters.title]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilters({ title: searchInput });
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const toggleCategory = useCallback((cat) => {
    const id = cat?._id?.toString?.() || cat?._id;
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSubcategory = useCallback((categoryId, subcategoryId) => {
    const key = `${categoryId}:${subcategoryId}`;
    setExpandedSubcategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleShowEdit = async (target) => {
    if (!target?._id || target?._id === 'limbo') return;
    try {
      const result = await getCategoryById(target._id);
      const canonicalTarget = normalizeEditableCategoryTarget(result?.data, target);
      setCategoryIdSelected(canonicalTarget);
    } catch (error) {
      console.error(error);
      setCategoryIdSelected(target);
      toast.error(t('catalog.loadError', { message: error?.message || 'Error loading category' }) || 'Error loading category');
    } finally {
      setIsVisibleModalEdit(true);
    }
  };

  const handleEditCategory = async () => {
    if (!editTarget) return;
    const json = {
      _id: editTarget._id,
      translations: editTarget.translations,
      emoji: editTarget.emoji,
    };
    const result = await updateCategory(json);
    if (result?.data?._id) {
      const updatedEntity = normalizeEditableCategoryTarget(result.data, editTarget);
      const newTitle = updatedEntity.translations?.find((t) => t?.lang === 'en')?.trans || updatedEntity.title || '';
      const optimisticGroups = JSON.parse(JSON.stringify(catalogCategoryGroups));
      const idStr = String(updatedEntity._id);
      const isSubcategory = !!updatedEntity.parent;
      const parentIdStr = updatedEntity.parent ? String(updatedEntity.parent) : null;

      if (isSubcategory && parentIdStr) {
        const cat = optimisticGroups.find((c) => String(c.categoryId) === parentIdStr);
        const sub = cat?.subcategories?.find((s) => String(s._id ?? s.subCategoryId) === idStr);
        if (sub) {
          sub.subCategoryTitle = newTitle;
          sub.subCategoryEmoji = updatedEntity.emoji ?? sub.subCategoryEmoji;
          sub.emoji = updatedEntity.emoji ?? sub.emoji;
          const subIdx = cat.subcategories.indexOf(sub);
          if (subIdx > 0) {
            cat.subcategories.splice(subIdx, 1);
            cat.subcategories.unshift(sub);
          }
        }
      } else {
        const cat = optimisticGroups.find((c) => String(c.categoryId) === idStr);
        if (cat) {
          cat.categoryTitle = newTitle;
          cat.categoryEmoji = updatedEntity.emoji ?? cat.categoryEmoji;
          cat.emoji = updatedEntity.emoji ?? cat.emoji;
          const catIdx = optimisticGroups.indexOf(cat);
          if (catIdx > 0) {
            optimisticGroups.splice(catIdx, 1);
            optimisticGroups.unshift(cat);
          }
        }
      }

      setCatalogCategoryGroups(optimisticGroups);
      dispatch(
        updateCategoryOrSubcategoryInPlace({
          _id: updatedEntity._id,
          translations: updatedEntity.translations,
          emoji: updatedEntity.emoji,
          parent: updatedEntity.parent,
        })
      );
      dispatch(
        setCatalogSnapshot({
          filtersKey,
          categoryGroups: optimisticGroups,
          totalPages: catalogTotalPages,
          currentPage: catalogCurrentPage,
          expandedCategoryIds: Array.from(expandedCategoryIds),
          expandedSubcategoryIds: Array.from(expandedSubcategoryIds),
          newContentItems,
          scrollPosition: (() => {
            const c = catalogRootRef.current ? getScrollContainer(catalogRootRef.current) : null;
            return c ? c.scrollTop : window.scrollY;
          })(),
        })
      );
      dispatch(fetchDecks());
      dispatch(fetchCategory());
      toast.success(t('toast.categoryUpdated'));
      setIsVisibleModalEdit(false);
      setCategoryIdSelected(null);
    } else {
      toast.error(result?.data?.message || t('toast.errorUpdatingCategory'));
    }
  };

  const handleTrad = (lang) => {
    const url = `${process.env.REACT_APP_API_URL}/translation/deepl`;
    const token = JSON.parse(localStorage?.getItem('user') || '{}')?.token;
    if (!token) return;
    fetch(url, {
      method: 'POST',
      body: JSON.stringify({
        title: editTarget?.translations?.filter((c) => c?.lang === 'en')?.[0]?.trans,
        lang,
      }),
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        const translation = data?.translations?.[0]?.text;
        if (translation)
          setCategoryIdSelected({
            ...editTarget,
            translations: [
              ...(editTarget?.translations?.filter((c) => c?.lang !== lang?.toLowerCase()) || []),
              { lang: lang?.toLowerCase(), trans: translation },
            ],
          });
      })
      .catch(console.error);
  };

  const handleCreateCategory = async () => {
    const payload = {
      type: 'Category',
      title: newCategoryForm.translations.find((t) => t.lang === 'en')?.trans || newCategoryForm.title,
      emoji: newCategoryForm.emoji || '📁',
      translations: newCategoryForm.translations,
    };
    const result = await createCategory(payload);
    const isError = result?.response != null;
    const errorMsg = result?.response?.data?.message || result?.data?.message;
    if (!isError && result?.data?._id) {
      const newCat = result.data;
      const title = newCat.title || newCategoryForm.translations.find((t) => t.lang === 'en')?.trans || '';
      const newGroup = {
        categoryId: newCat._id,
        categoryTitle: title,
        emoji: newCat.emoji || newCategoryForm.emoji || '📁',
        subcategories: [],
      };
      setCatalogCategoryGroups((prev) => [newGroup, ...prev]);
      dispatch(fetchCategory());
      dispatch(
        setCatalogSnapshot({
          filtersKey,
          categoryGroups: [newGroup, ...catalogCategoryGroups],
          totalPages: catalogTotalPages,
          currentPage: catalogCurrentPage,
          expandedCategoryIds: Array.from(expandedCategoryIds),
          expandedSubcategoryIds: Array.from(expandedSubcategoryIds),
          newContentItems,
          scrollPosition: (() => {
            const c = catalogRootRef.current ? getScrollContainer(catalogRootRef.current) : null;
            return c ? c.scrollTop : window.scrollY;
          })(),
        })
      );
      toast.success(t('toast.categoryCreated'));
      setIsVisibleModalNewCategory(false);
      setNewCategoryForm({ title: '', emoji: '', translations: defaultTranslations() });
    } else {
      toast.error(Array.isArray(errorMsg) ? errorMsg[0] : errorMsg || t('toast.errorCreatingCategory'));
    }
  };

  const handleShowNewSubcategory = (parentCategory) => {
    setNewSubcategoryForm({
      title: '',
      emoji: '',
      translations: defaultTranslations(),
      parent: parentCategory?._id,
    });
    setIsVisibleModalNewSubcategory(true);
  };

  const handleCreateSubcategory = async () => {
    const payload = {
      type: 'Subcategory',
      parent: newSubcategoryForm.parent,
      title: newSubcategoryForm.translations.find((t) => t.lang === 'en')?.trans || newSubcategoryForm.title,
      emoji: newSubcategoryForm.emoji || '📂',
      translations: newSubcategoryForm.translations,
    };
    const result = await createCategory(payload);
    const isError = result?.response != null;
    const errorMsg = result?.response?.data?.message || result?.data?.message;
    if (!isError && result?.data?._id) {
      const newSub = result.data;
      const title = newSub.title || newSubcategoryForm.translations.find((t) => t.lang === 'en')?.trans || '';
      const parentIdStr = String(newSubcategoryForm.parent || newSub.parent);
      const newSubcat = {
        subCategoryId: newSub._id,
        _id: newSub._id,
        subCategoryTitle: title,
        emoji: newSub.emoji || newSubcategoryForm.emoji || '📂',
        items: [],
      };
      const optimisticGroups = JSON.parse(JSON.stringify(catalogCategoryGroups));
      const parentCat = optimisticGroups.find((c) => String(c.categoryId) === parentIdStr);
      if (parentCat) {
        parentCat.subcategories = parentCat.subcategories || [];
        parentCat.subcategories.unshift(newSubcat);
      }
      setCatalogCategoryGroups(optimisticGroups);
      dispatch(fetchCategory());
      dispatch(
        setCatalogSnapshot({
          filtersKey,
          categoryGroups: optimisticGroups,
          totalPages: catalogTotalPages,
          currentPage: catalogCurrentPage,
          expandedCategoryIds: Array.from(new Set([...expandedCategoryIds, parentIdStr])),
          expandedSubcategoryIds: Array.from(expandedSubcategoryIds),
          newContentItems,
          scrollPosition: (() => {
            const c = catalogRootRef.current ? getScrollContainer(catalogRootRef.current) : null;
            return c ? c.scrollTop : window.scrollY;
          })(),
        })
      );
      setExpandedCategoryIds((prev) => new Set([...prev, parentIdStr]));
      toast.success(t('toast.subcategoryCreated'));
      setIsVisibleModalNewSubcategory(false);
      setNewSubcategoryForm({ title: '', emoji: '', translations: defaultTranslations(), parent: null });
    } else {
      toast.error(Array.isArray(errorMsg) ? errorMsg[0] : errorMsg || t('toast.errorCreatingSubcategory'));
    }
  };

  const handleDeleteCategoryConfirm = async () => {
    if (!deleteCatModalState.target) return;
    setIsDeletingCat(true);
    const target = deleteCatModalState.target;
    const targetId = target._id;
    const isSubcat = !!target.parent;
    const parentIdStr = target.parent ? String(target.parent) : null;
    try {
      const result = await deleteCategory(targetId);
      const isError = result?.response != null;
      const errorCode = result?.response?.data?.code;
      const errorMsg = result?.response?.data?.message || result?.data?.message;
      if (!isError) {
        toast.success(isSubcat ? t('toast.subcategoryDeleted') || 'Subcategoría eliminada' : t('toast.categoryDeleted') || 'Categoría eliminada');
        setDeleteCatModalState({ visible: false, target: null });

        // Compute new groups (remove from DOM without reload or scroll)
        const newGroups = isSubcat && parentIdStr
          ? catalogCategoryGroups.map((cat) => {
              if (String(cat.categoryId) !== parentIdStr) return cat;
              const filtered = (cat.subcategories || []).filter(
                (s) => String(s.subCategoryId ?? s._id) !== String(targetId)
              );
              return { ...cat, subcategories: filtered };
            })
          : catalogCategoryGroups.filter((c) => String(c.categoryId) !== String(targetId));

        setCatalogCategoryGroups(newGroups);

        // Remove from expanded sets
        setExpandedSubcategoryIds((prev) => {
          const next = new Set(prev);
          if (isSubcat && parentIdStr) next.delete(`${parentIdStr}:${targetId}`);
          return next;
        });
        if (!isSubcat) setExpandedCategoryIds((prev) => new Set([...prev].filter((id) => id !== String(targetId))));

        // Update Redux catalog snapshot and category list
        const container = catalogRootRef.current ? getScrollContainer(catalogRootRef.current) : null;
        const scrollPos = container ? container.scrollTop : window.scrollY;
        const newExpandedCats = !isSubcat ? [...expandedCategoryIds].filter((id) => id !== String(targetId)) : [...expandedCategoryIds];
        const newExpandedSubs = isSubcat && parentIdStr
          ? [...expandedSubcategoryIds].filter((k) => k !== `${parentIdStr}:${targetId}`)
          : [...expandedSubcategoryIds];
        dispatch(setCatalogSnapshot({
          filtersKey,
          categoryGroups: newGroups,
          totalPages: catalogTotalPages,
          currentPage: catalogCurrentPage,
          expandedCategoryIds: newExpandedCats,
          expandedSubcategoryIds: newExpandedSubs,
          newContentItems,
          scrollPosition: scrollPos,
        }));
        dispatch(removeCategoryOrSubcategory({ _id: targetId, parent: target.parent }));
      } else {
        const translatedMsg =
          errorCode === 'SUBCATEGORY_HAS_ITEMS'
            ? t('toast.subcategoryHasItems')
            : errorCode === 'CATEGORY_HAS_SUBCATEGORIES'
              ? t('toast.categoryHasSubcategories')
              : errorCode === 'CATEGORY_HAS_ITEMS'
                ? t('toast.categoryHasItems')
                : Array.isArray(errorMsg)
                  ? errorMsg[0]
                  : errorMsg || t('toast.errorDeletingCategory');
        toast.error(translatedMsg);
      }
    } catch (error) {
      const errorCode = error?.response?.data?.code;
      const msg =
        errorCode === 'SUBCATEGORY_HAS_ITEMS'
          ? t('toast.subcategoryHasItems')
          : errorCode === 'CATEGORY_HAS_SUBCATEGORIES'
            ? t('toast.categoryHasSubcategories')
            : errorCode === 'CATEGORY_HAS_ITEMS'
              ? t('toast.categoryHasItems')
              : error?.response?.data?.message || error?.message || t('toast.errorDeletingCategory');
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setIsDeletingCat(false);
    }
  };

  const handleAddDeck = async () => {
    if (!deckSelected || !categoryIdSelected) return;
    const target = categoryIdSelected?.parent ? categoryIdSelected : categoryIdSelected;
    const json = {
      _id: deckSelected?.value || deckSelected?._id,
      type: 'deck',
      category: (target?.parent || target?._id)?.toString(),
      subCategory: categoryIdSelected?.parent ? categoryIdSelected._id?.toString() : undefined,
    };
    const result = await updateCatalogItem(json);
    if (result?.data) {
      dispatch(fetchDecks());
      loadCategoriesFromCatalog();
      toast.success(t('toast.deckAdded'));
      setIsVisibleModalAddDeck(false);
      setDeckSelected(null);
      setCategoryIdSelected(null);
    } else {
      toast.error(result?.data?.message || t('common.error'));
    }
  };

  const handleDeleteDeck = async () => {
    if (!deckSelected) return;
    const id = deckSelected._id || deckSelected.sourceId;
    const json = { _id: id, type: 'deck', category: null, subCategory: null };
    const result = await updateCatalogItem(json);
    if (result?.data) {
      dispatch(fetchDecks());
      loadCategoriesFromCatalog();
      toast.success(t('toast.deckRemoved'));
      setIsVisibleModalDeleteDeck(false);
      setDeckSelected(null);
      setCategoryIdSelected(null);
    } else {
      toast.error(result?.data?.message || t('common.error'));
    }
  };

  /** Find which category/subcategory contains this item (by sourceId or catalogRef). */
  const findItemSource = useCallback((groups, item) => {
    const id = String(item.sourceId ?? item.catalogRef?.split(':')?.[1] ?? '');
    if (!id) return null;
    for (const cat of groups) {
      for (const sub of cat.subcategories || []) {
        const found = (sub.items || []).find(
          (i) => String(i.sourceId ?? i.catalogRef?.split(':')?.[1] ?? '') === id
        );
        if (found) return { category: cat, subcategory: sub, item: found };
      }
    }
    return null;
  }, []);

  /** Sort items by the active catalog order (name or lastUpdate). */
  const sortItemsByOrder = useCallback((items, order) => {
    if (!items?.length || !order) return items;
    const sorted = [...items];
    if (order === 'lastUpdate') {
      sorted.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.lastUpdate || 0).getTime();
        const dateB = new Date(b.updatedAt || b.lastUpdate || 0).getTime();
        return dateB - dateA;
      });
    } else {
      sorted.sort((a, b) => {
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });
    }
    return sorted;
  }, []);

  const moveItemsToSubcategory = useCallback(
    async (items, targetSubcategoryId, targetCategoryId) => {
      if (moveInProgressRef.current || !items?.length) return;

      const getId = (i) => String(i.sourceId ?? i.catalogRef?.split(':')?.[1] ?? '');
      const normalizeId = (id) => (id == null ? '' : String(id));
      const matchSubId = (s, subId) =>
        normalizeId(s.subCategoryId ?? '__limbo__') === normalizeId(subId);

      const previousGroups = JSON.parse(JSON.stringify(catalogCategoryGroups));
      const optimisticGroups = JSON.parse(JSON.stringify(catalogCategoryGroups));
      const targetCatId = String(targetCategoryId);
      const targetSubId = String(targetSubcategoryId);
      const addTo = optimisticGroups
        .find((c) => normalizeId(c.categoryId) === normalizeId(targetCatId))
        ?.subcategories?.find((s) => matchSubId(s, targetSubId));

      if (!addTo) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[moveItemsToSubcategory] Could not find target subcategory', { targetCatId, targetSubId });
        }
        return;
      }

      moveInProgressRef.current = true;
      const idsToMove = new Set(items.map((i) => getId(i)).filter(Boolean));

      const itemsToAdd = [];
      for (const item of items) {
        const source = findItemSource(catalogCategoryGroups, item);
        if (!source) continue;
        const sourceCatId = String(source.category.categoryId);
        const sourceSubId = String(source.subcategory.subCategoryId);
        if (sourceCatId === targetCatId && sourceSubId === targetSubId) continue;

        const removeFrom = optimisticGroups
          .find((c) => normalizeId(c.categoryId) === normalizeId(sourceCatId))
          ?.subcategories?.find((s) => matchSubId(s, sourceSubId));
        if (removeFrom) {
          removeFrom.items = (removeFrom.items || []).filter((i) => !idsToMove.has(getId(i)));
        }
        itemsToAdd.push({ ...source.item });
      }

      const addToWithoutMoved = (addTo.items || []).filter((i) => !idsToMove.has(getId(i)));
      const newItems = [...addToWithoutMoved, ...itemsToAdd];
      addTo.items = sortItemsByOrder(newItems, catalogParams.order);

      setCatalogCategoryGroups(optimisticGroups);

      const isLimboTarget = targetSubId === 'limbo' || targetSubId === '__limbo__';
      const payloads = itemsToAdd.map((item) => ({
        _id: getId(item),
        type: item.type === 'deck' ? 'deck' : 'text',
        category: targetCategoryId,
        subCategory: isLimboTarget ? null : targetSubcategoryId,
      }));

      try {
        const results = await Promise.all(payloads.map((p) => updateCatalogItem(p)));
        const allOk = results.every((r) => r?.data?._id || r?.data);
        if (allOk) {
          const subName = optimisticGroups
            .flatMap((c) => c.subcategories || [])
            .find((s) => normalizeId(s.subCategoryId) === normalizeId(targetSubId))?.subCategoryTitle || t('catalog.subcategory');
          toast.success(
            itemsToAdd.length > 1
              ? (t('catalog.itemsMovedTo', { count: itemsToAdd.length, name: subName }) || `${itemsToAdd.length} items moved to ${subName}`)
              : (t('toast.itemMovedTo', { name: subName }) || `Item moved to ${subName}`)
          );
          if (itemsToAdd.some((i) => i.type === 'deck')) dispatch(fetchDecks());
          dispatch(
            setCatalogSnapshot({
              filtersKey,
              categoryGroups: optimisticGroups,
              totalPages: catalogTotalPages,
              currentPage: catalogCurrentPage,
              expandedCategoryIds: Array.from(expandedCategoryIds),
              expandedSubcategoryIds: Array.from(expandedSubcategoryIds),
              newContentItems,
              scrollPosition: (() => {
                const c = catalogRootRef.current ? getScrollContainer(catalogRootRef.current) : null;
                return c ? c.scrollTop : window.scrollY;
              })(),
            })
          );
        } else {
          setCatalogCategoryGroups(previousGroups);
          toast.error(results.find((r) => !r?.data?._id && !r?.data)?.data?.message || t('toast.failedToMoveItem'));
        }
      } catch (e) {
        setCatalogCategoryGroups(previousGroups);
        toast.error(e?.response?.data?.message || e?.message || t('toast.failedToMoveItem'));
      } finally {
        moveInProgressRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogCategoryGroups, catalogCurrentPage, catalogTotalPages, catalogParams.order, expandedCategoryIds, expandedSubcategoryIds, filtersKey, newContentItems, dispatch, findItemSource, sortItemsByOrder, t]
  );

  const moveSubcategoryToCategory = useCallback(
    async (subcategoryId, targetCategoryId) => {
      try {
        const result = await updateCategory({ _id: subcategoryId, parent: targetCategoryId });
        if (result?.data?._id) {
          // Bug 1 fix: build optimistic groups — move the subcategory object to the target category locally
          // instead of calling loadCategoriesFromCatalog() which would reset everything.
          const subIdStr = String(subcategoryId);
          const optimisticGroups = JSON.parse(JSON.stringify(catalogCategoryGroups));
          let movedSub = null;
          // Remove subcategory from its current parent
          for (const cat of optimisticGroups) {
            const idx = (cat.subcategories || []).findIndex((s) => String(s._id ?? s.subCategoryId) === subIdStr);
            if (idx !== -1) {
              [movedSub] = cat.subcategories.splice(idx, 1);
              break;
            }
          }
          // Add it to the target category
          if (movedSub) {
            const targetCat = optimisticGroups.find((c) => String(c.categoryId) === String(targetCategoryId));
            if (targetCat) {
              targetCat.subcategories = [...(targetCat.subcategories || []), movedSub];
            }
          }
          setCatalogCategoryGroups(optimisticGroups);
          dispatch(
            setCatalogSnapshot({
              filtersKey,
              categoryGroups: optimisticGroups,
              totalPages: catalogTotalPages,
              currentPage: catalogCurrentPage,
              expandedCategoryIds: Array.from(expandedCategoryIds),
              expandedSubcategoryIds: Array.from(expandedSubcategoryIds),
              newContentItems,
              scrollPosition: (() => {
                const c = catalogRootRef.current ? getScrollContainer(catalogRootRef.current) : null;
                return c ? c.scrollTop : window.scrollY;
              })(),
            })
          );
          const catName = catalogCategoryGroups.find((c) => String(c.categoryId) === String(targetCategoryId))?.categoryTitle || 'category';
          setSubcategoryMovedLabel((prev) => ({ ...prev, [subcategoryId]: catName }));
          setTimeout(() => setSubcategoryMovedLabel((prev) => ({ ...prev, [subcategoryId]: undefined })), 3000);
        } else {
          toast.error(result?.data?.message || t('toast.failedToMoveSubcategory'));
        }
      } catch (e) {
        toast.error(e?.message || t('toast.failedToMoveSubcategory'));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogCategoryGroups, catalogCurrentPage, catalogTotalPages, expandedCategoryIds, expandedSubcategoryIds, filtersKey, newContentItems, dispatch]
  );

  const sensors = useSensors(
    useSensor(CatalogPointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = (event) => {
    const { active } = event;
    const data = active?.data?.current;
    if (data?.type === 'item') {
      const item = data.item;
      const itemId = getItemId(item);
      const itemsToDrag = selectedItemIds.has(itemId)
        ? getSelectedItemsFromGroups(catalogCategoryGroups, selectedItemIds)
        : [item];
      setActiveDragItem(item);
      setActiveDragItems(itemsToDrag);
    }
    if (data?.type === 'subcategory') setActiveDragSubcategory(data.subcategory);
    if (data?.type === 'category') {
      const cat = catalogCategoryGroups.find((c) => String(c.categoryId) === String(active.id));
      if (cat) {
        setActiveDragCategory({ categoryId: cat.categoryId, categoryTitle: cat.categoryTitle, emoji: cat.emoji });
        categoryDragSnapshotRef.current = JSON.parse(JSON.stringify(catalogCategoryGroups));
      }
    }
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    const activeData = active?.data?.current;
    if (activeData?.type !== 'category' || !over || hasFilterOtherThanType) return;
    const activeCatId = getCategoryIdFromOverId(active.id);
    const overCatId = getCategoryIdFromOverId(over.id);
    const oldIndex = catalogCategoryGroups.findIndex((c) => String(c.categoryId) === activeCatId);
    const newIndex = catalogCategoryGroups.findIndex((c) => String(c.categoryId) === overCatId);
    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      const newOrder = arrayMove(catalogCategoryGroups, oldIndex, newIndex);
      setCatalogCategoryGroups(newOrder);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    const activeData = active?.data?.current;
    const itemsToMove = activeData?.type === 'item' ? activeDragItems : [];
    setActiveDragItem(null);
    setActiveDragItems([]);
    setActiveDragSubcategory(null);
    setActiveDragCategory(null);
    if (!over) {
      if (activeData?.type === 'category' && categoryDragSnapshotRef.current) {
        setCatalogCategoryGroups(categoryDragSnapshotRef.current);
        categoryDragSnapshotRef.current = null;
      }
      return;
    }
    const overData = over?.data?.current;

    // Category reorder (sortable)
    if (activeData?.type === 'category' && !hasFilterOtherThanType) {
      const activeCatId = getCategoryIdFromOverId(active.id);
      const overCatId = getCategoryIdFromOverId(over.id);
      const oldIndex = catalogCategoryGroups.findIndex((c) => String(c.categoryId) === activeCatId);
      const newIndex = catalogCategoryGroups.findIndex((c) => String(c.categoryId) === overCatId);
      // Use current order (may have been updated by onDragOver); if indices valid and different, use arrayMove
      const orderToSend = (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex)
        ? arrayMove(catalogCategoryGroups, oldIndex, newIndex)
        : catalogCategoryGroups;
      const reorderedIds = new Set(orderToSend.map((c) => String(c.categoryId)));
      const restFromApi = (categoriesFromApi || [])
        .filter((c) => c._id && !reorderedIds.has(String(c._id)))
        .map((c) => String(c._id));
      const categoryIds = [...orderToSend.map((c) => String(c.categoryId)), ...restFromApi];

      if (categoryIds.length > 0) {
        try {
          const result = await reorderCategoriesApi(categoryIds);
          const isOk = !result?.response && result?.data?.ok === true;
          if (isOk) {
            categoryDragSnapshotRef.current = null;
            dispatch(reorderCategories({ categoryIds }));
            dispatch(fetchCategory());
            loadCategoriesFromCatalog(true);
            toast.success(t('toast.categoriesReordered') || t('toast.categoryUpdated') || 'Orden de categorías guardado correctamente');
          } else {
            if (categoryDragSnapshotRef.current) {
              setCatalogCategoryGroups(categoryDragSnapshotRef.current);
            } else {
              loadCategoriesFromCatalog(true);
            }
            categoryDragSnapshotRef.current = null;
            const errMsg = result?.response?.data?.message || result?.message;
            toast.error(errMsg || t('toast.failedToReorderCategories') || t('toast.errorUpdatingCategory') || 'Error al guardar el orden');
          }
        } catch (err) {
          if (categoryDragSnapshotRef.current) {
            setCatalogCategoryGroups(categoryDragSnapshotRef.current);
          } else {
            loadCategoriesFromCatalog(true);
          }
          categoryDragSnapshotRef.current = null;
          toast.error(err?.response?.data?.message || err?.message || t('toast.failedToReorderCategories') || 'Error al guardar el orden');
        }
      } else {
        categoryDragSnapshotRef.current = null;
      }
      return;
    }

    if (activeData?.type === 'item' && overData?.type === 'subcategory' && itemsToMove.length > 0) {
      moveItemsToSubcategory(itemsToMove, overData.subcategoryId, overData.categoryId);
      setSelectedItemIds(new Set());
    }
    if (activeData?.type === 'subcategory' && overData?.type === 'category') {
      const subId = activeData.subcategory?._id?.toString?.() || activeData.subcategory?._id;
      const currentParent = (activeData.subcategory?.parent?._id ?? activeData.subcategory?.parent)?.toString?.();
      if (subId && overData.categoryId && overData.categoryId !== currentParent) {
        moveSubcategoryToCategory(subId, overData.categoryId);
      }
    }
  };

  const activeFilterCount = [
    filters.title,
    filters.level,
    filters.type !== 'all',
    filters.premiumFilter !== 'all',
    filters.newContent,
    filters.available !== 'all',
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  /** When true, we reorder categories (with results first, empty at end) and keep empty ones collapsed. */
  const hasFilterOtherThanType = !!(
    filters.title ||
    filters.level ||
    filters.premiumFilter !== 'all' ||
    filters.newContent ||
    filters.available !== 'all'
  );

  const visibleCategories = useMemo(() => {
    if (!hasFilterOtherThanType) return catalogCategoryGroups;
    return [...catalogCategoryGroups].sort((a, b) => {
      const aHas = categoryHasItems(a);
      const bHas = categoryHasItems(b);
      if (aHas === bHas) return 0;
      return aHas ? -1 : 1;
    });
  }, [catalogCategoryGroups, hasFilterOtherThanType]);

  const flatItemsOrdered = useMemo(() => {
    const items = [];
    visibleCategories.forEach((cat) => {
      const subs = hasFilterOtherThanType
        ? [...(cat.subcategories || [])].sort((a, b) => {
            const aHas = (a.items || []).length > 0;
            const bHas = (b.items || []).length > 0;
            if (aHas === bHas) return 0;
            return aHas ? -1 : 1;
          })
        : cat.subcategories || [];
      subs.forEach((sub) => {
        (sub.items || []).forEach((it) => items.push(it));
      });
    });
    return items;
  }, [visibleCategories, hasFilterOtherThanType]);

  const handleItemClick = useCallback((item, flatIndex, e) => {
    const itemId = getItemId(item);
    const metaOrCtrl = e.metaKey || e.ctrlKey;
    const shift = e.shiftKey;

    if (metaOrCtrl) {
      setSelectedItemIds((prev) => {
        const next = new Set(prev);
        if (next.has(itemId)) next.delete(itemId);
        else next.add(itemId);
        lastSelectedFlatIndexRef.current = next.has(itemId) ? flatIndex : -1;
        return next;
      });
      e.stopPropagation();
      return;
    }
    if (shift) {
      // Si no hay ancla previa, el primer shift+click selecciona solo ese ítem y lo establece como ancla
      const from = lastSelectedFlatIndexRef.current >= 0 ? lastSelectedFlatIndexRef.current : flatIndex;
      const to = flatIndex;
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      const idsToAdd = new Set();
      for (let i = lo; i <= hi && i < flatItemsOrdered.length; i++) {
        idsToAdd.add(getItemId(flatItemsOrdered[i]));
      }
      setSelectedItemIds((prev) => {
        const next = new Set(prev);
        idsToAdd.forEach((id) => next.add(id));
        lastSelectedFlatIndexRef.current = flatIndex;
        return next;
      });
      e.stopPropagation();
      return;
    }
    if (selectedItemIds.has(itemId)) {
      // Windows behavior: click on selected item = select only that item
      setSelectedItemIds(new Set([itemId]));
      lastSelectedFlatIndexRef.current = flatIndex;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setSelectedItemIds(new Set([itemId]));
    lastSelectedFlatIndexRef.current = flatIndex;
    e.preventDefault();
    e.stopPropagation();
  }, [selectedItemIds, flatItemsOrdered]);

  const clearSelection = useCallback(() => {
    setSelectedItemIds(new Set());
    lastSelectedFlatIndexRef.current = -1;
  }, []);

  const getImageSrc = (item) => {
    const url = item?.imageUrl;
    if (!url || url.includes('undefined')) {
      return item?.type === 'text' ? getPlaceholderImageUrl(item) : DEFAULT_DECK_AVATAR;
    }
    return url;
  };

  // With filters (level, premium, search, etc.): only expand categories and subcategories that have items
  useEffect(() => {
    if (!hasFilterOtherThanType || catalogCategoryGroups.length === 0) return;
    const withItems = catalogCategoryGroups.filter(categoryHasItems).map((c) => c.categoryId);
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      next.forEach((id) => {
        const cat = catalogCategoryGroups.find((c) => c.categoryId === id);
        if (cat && !categoryHasItems(cat)) next.delete(id);
      });
      withItems.forEach((id) => next.add(id));
      return next;
    });
    setExpandedSubcategoryIds((prev) => {
      const next = new Set(prev);
      catalogCategoryGroups.forEach((cat) => {
        (cat.subcategories || []).forEach((sub) => {
          const key = `${cat.categoryId}:${sub.subCategoryId || '__limbo__'}`;
          const hasSubItems = (sub.items || []).length > 0;
          if (hasSubItems) next.add(key);
          else next.delete(key);
        });
      });
      return next;
    });
  }, [hasFilterOtherThanType, catalogCategoryGroups]);

  const hasMoreCategories = catalogCurrentPage >= 0 && catalogCurrentPage + 1 < catalogTotalPages;

  return (
    <div ref={catalogRootRef} className={styles.catalogRoot}>
      <Card isStyle={false}>
        {/* ── Modals ── */}
      <Modal
        outerClassName={styles.outer}
        visible={isVisibleModalEdit}
        onClose={() => {
          if (emojiPickerTarget) {
            return;
          }
          setIsVisibleModalEdit(false);
          setCategoryIdSelected(null);
          setEmojiPickerTarget(null);
        }}
      >
        <div style={{ fontSize: 20, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {editTarget?.emoji && <span style={{ fontSize: '24px' }}>{editTarget.emoji}</span>}
          <span>
            {editTarget?.parent ? t('catalog.editSubcategory') : t('catalog.editCategory')} <b>{editTarget?.title || t('common.untitled')}</b>
          </span>
        </div>
        <div style={{ marginTop: 20 }}>
          {['en', 'es', 'fr', 'ru', 'pt'].map((lang) => (
            <div key={lang} style={{ marginTop: 10, position: 'relative' }}>
              <TextInput
                type="text"
                currency={lang.toUpperCase()}
                value={editTarget?.translations?.find((c) => c?.lang === lang)?.trans || ''}
                onChange={(e) =>
                  setCategoryIdSelected({
                    ...editTarget,
                    translations: [
                      ...(editTarget?.translations?.filter((c) => c?.lang !== lang) || []),
                      { lang, trans: e.target.value },
                    ],
                  })
                }
              />
              {lang !== 'en' && (
                <div
                  style={{
                    position: 'absolute',
                    right: 20,
                    top: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <button type="button" onClick={() => handleTrad(lang)}>
                    <FiRepeat size={20} />
                  </button>
                </div>
              )}
            </div>
          ))}
          <div style={{ marginTop: 10 }}>
            <TextInput
              placeholder={t('common.emoji')}
              currency="🎯"
              value={editTarget?.emoji || ''}
              onChange={(e) => setCategoryIdSelected({ ...editTarget, emoji: e.target.value })}
              onClick={() => setEmojiPickerTarget('edit-category')}
              readOnly
            />
          </div>
          <button
            type="button"
            onClick={handleEditCategory}
            style={{ width: '100%', marginTop: 30 }}
            className={classNames('button-small', styles.button)}
          >
            {t('common.update')}
          </button>
        </div>
      </Modal>

      <Modal
        outerClassName={styles.outer}
        visible={isVisibleModalNewCategory}
        onClose={() => {
          if (emojiPickerTarget) {
            return;
          }
          setIsVisibleModalNewCategory(false);
          setEmojiPickerTarget(null);
        }}
      >
        <div style={{ fontSize: 20 }}>{t('catalog.newCategory')}</div>
        <div style={{ marginTop: 20 }}>
          {['en', 'es', 'fr', 'ru', 'pt'].map((lang) => (
            <div key={lang} style={{ marginTop: 10, position: 'relative' }}>
              <TextInput
                placeholder={`${t('common.title')} (${lang.toUpperCase()})`}
                currency={lang.toUpperCase()}
                value={newCategoryForm.translations.find((tr) => tr.lang === lang)?.trans || ''}
                onChange={(e) =>
                  setNewCategoryForm({
                    ...newCategoryForm,
                    translations: newCategoryForm.translations.map((tr) =>
                      tr.lang === lang ? { ...tr, trans: e.target.value } : tr
                    ),
                  })
                }
              />
              {lang !== 'en' && (
                <div
                  style={{
                    position: 'absolute',
                    right: 20,
                    top: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <button type="button" onClick={() => {
                      const url = `${process.env.REACT_APP_API_URL}/translation/deepl`;
                      const token = JSON.parse(localStorage?.getItem('user') || '{}')?.token;
                      if (!token) return;
                      fetch(url, {
                        method: 'POST',
                        body: JSON.stringify({
                          title: newCategoryForm?.translations?.find((c) => c?.lang === 'en')?.trans,
                          lang,
                        }),
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      })
                        .then((r) => r.json())
                        .then((data) => {
                          const translation = data?.translations?.[0]?.text;
                          if (translation)
                            setNewCategoryForm((prev) => ({
                              ...prev,
                              translations: prev.translations.map((tr) =>
                                tr.lang === lang ? { ...tr, trans: translation } : tr
                              ),
                            }));
                        })
                        .catch(console.error);
                  }}>
                    <FiRepeat size={20} />
                  </button>
                </div>
              )}
            </div>
          ))}
          <div style={{ marginTop: 10 }}>
            <TextInput
              placeholder={t('common.emoji')}
              currency="🎯"
              value={newCategoryForm.emoji}
              onChange={(e) => setNewCategoryForm({ ...newCategoryForm, emoji: e.target.value })}
              onClick={() => setEmojiPickerTarget('new-category')}
              readOnly
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button
              type="button"
              className={classNames('button-stroke button-small')}
              onClick={() => setEmojiPickerTarget('new-category')}
            >
              {t('common.emoji')}
            </button>
          </div>
          <button
            type="button"
            onClick={handleCreateCategory}
            style={{ width: '100%', marginTop: 30 }}
            className={classNames('button-small', styles.button)}
          >
            {t('common.create')}
          </button>
        </div>
      </Modal>

      <Modal
        outerClassName={styles.outer}
        visible={!!emojiPickerTarget}
        onClose={() => setEmojiPickerTarget(null)}
      >
        <div style={{ fontSize: 18, marginBottom: 12 }}>{t('common.emoji')}</div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <EmojiPicker
            theme={darkMode.value ? EmojiTheme.DARK : EmojiTheme.LIGHT}
            onEmojiClick={(emojiData, event) => {
              event?.stopPropagation?.();
              const selectedEmoji = emojiData?.emoji;
              if (!selectedEmoji) return;
              if (emojiPickerTarget === 'new-category') {
                setNewCategoryForm((prev) => ({ ...prev, emoji: selectedEmoji }));
              } else if (emojiPickerTarget === 'new-subcategory') {
                setNewSubcategoryForm((prev) => ({ ...prev, emoji: selectedEmoji }));
              } else if (emojiPickerTarget === 'edit-category') {
                setCategoryIdSelected((prev) => (prev ? { ...prev, emoji: selectedEmoji } : prev));
              }
              setEmojiPickerTarget(null);
            }}
          />
        </div>
      </Modal>

      <Modal
        outerClassName={styles.outer}
        visible={isVisibleModalNewSubcategory}
        onClose={() => {
          if (emojiPickerTarget) {
            return;
          }
          setIsVisibleModalNewSubcategory(false);
          setEmojiPickerTarget(null);
        }}
      >
        <div style={{ fontSize: 20 }}>{t('catalog.newSubcategory')}</div>
        <div style={{ marginTop: 20 }}>
          {['en', 'es', 'fr', 'ru', 'pt'].map((lang) => (
            <div key={lang} style={{ marginTop: 10, position: 'relative' }}>
              <TextInput
                placeholder={`${t('common.title')} (${lang.toUpperCase()})`}
                currency={lang.toUpperCase()}
                value={newSubcategoryForm.translations.find((tr) => tr.lang === lang)?.trans || ''}
                onChange={(e) =>
                  setNewSubcategoryForm({
                    ...newSubcategoryForm,
                    translations: newSubcategoryForm.translations.map((tr) =>
                      tr.lang === lang ? { ...tr, trans: e.target.value } : tr
                    ),
                  })
                }
              />
              {lang !== 'en' && (
                <div
                  style={{
                    position: 'absolute',
                    right: 20,
                    top: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <button type="button" onClick={() => {
                      const url = `${process.env.REACT_APP_API_URL}/translation/deepl`;
                      const token = JSON.parse(localStorage?.getItem('user') || '{}')?.token;
                      if (!token) return;
                      fetch(url, {
                        method: 'POST',
                        body: JSON.stringify({
                          title: newSubcategoryForm?.translations?.find((c) => c?.lang === 'en')?.trans,
                          lang,
                        }),
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                      })
                        .then((r) => r.json())
                        .then((data) => {
                          const translation = data?.translations?.[0]?.text;
                          if (translation)
                            setNewSubcategoryForm((prev) => ({
                              ...prev,
                              translations: prev.translations.map((tr) =>
                                tr.lang === lang ? { ...tr, trans: translation } : tr
                              ),
                            }));
                        })
                        .catch(console.error);
                  }}>
                    <FiRepeat size={20} />
                  </button>
                </div>
              )}
            </div>
          ))}
          <div style={{ marginTop: 10 }}>
            <TextInput
              placeholder={t('common.emoji')}
              currency="🎯"
              value={newSubcategoryForm.emoji}
              onChange={(e) =>
                setNewSubcategoryForm({ ...newSubcategoryForm, emoji: e.target.value })
              }
              onClick={() => setEmojiPickerTarget('new-subcategory')}
              readOnly
            />
          </div>
          <button
            type="button"
            onClick={handleCreateSubcategory}
            style={{ width: '100%', marginTop: 30 }}
            className={classNames('button-small', styles.button)}
          >
            {t('common.create')}
          </button>
        </div>
      </Modal>

      <Modal
        outerClassName={styles.outer}
        visible={isVisibleModalDeleteDeck}
        onClose={() => {
          setIsVisibleModalDeleteDeck(false);
          setDeckSelected(null);
          setCategoryIdSelected(null);
        }}
      >
        <div style={{ fontSize: 20 }}>
          {t('catalog.removeDeckFrom')} <b>{deckSelected?.title}</b> {t('common.from')} {' '}
          <b>{categoryIdSelected?.title || t('common.untitled')}</b>
        </div>
        <div style={{ marginTop: 20 }}>
          <button
            type="button"
            onClick={handleDeleteDeck}
            className={classNames('button-stroke-red', styles.button)}
            style={{ width: '100%', marginTop: 30 }}
          >
            {t('common.removeDeck')}
          </button>
        </div>
      </Modal>

      <Modal
        outerClassName={styles.outer}
        visible={isVisibleModalAddDeck}
        onClose={() => {
          setIsVisibleModalAddDeck(false);
          setDeckSelected(null);
          setCategoryIdSelected(null);
        }}
      >
        <div style={{ fontSize: 20 }}>
          {t('catalog.addDeckTo')} <b>{categoryIdSelected?.title || t('common.untitled')}</b>
        </div>
        <div style={{ marginTop: 20 }}>
          <div className={styles.label}>{t('catalog.selectDeckLabel')}</div>
          <DropdownSearch
            className={styles.field}
            value={deckSelected?.value}
            placeholder={t('catalog.searchDecks')}
            setValue={setDeckSelected}
            options={(decks?.value || []).map((deck) => ({ label: deck?.title, value: deck?._id }))}
            isLoading={decks?.isLoading}
            timeoutSearch={500}
            onSearch={(e) => dispatch(fetchDecks({ take: 100, skip: 0, search: e }))}
          />
          <button
            type="button"
            disabled={!deckSelected}
            onClick={handleAddDeck}
            style={{ width: '100%', marginTop: 30 }}
            className={classNames('button-small', styles.button)}
          >
            {t('common.addDeck')}
          </button>
        </div>
      </Modal>

      {/* ── Floating Bottom Filter Bar ── */}
      <div className={styles.floatingFilterBar}>
        <div className={styles.filterMainRow}>
          <div className={styles.filterTabs}>
            {[
              { value: 'all', label: `🌐 ${t('common.all')}` },
              { value: 'deck', label: `🃏 ${t('catalog.decksType')}` },
              { value: 'text', label: `📄 ${t('catalog.textsType')}` },
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={classNames(styles.typeTab, { [styles.active]: filters.type === value })}
                onClick={() => setFilters({ type: value })}
              >
                {label}
              </button>
            ))}
          </div>

          <SearchInput
            className={styles.filterSearch}
            placeholder={t('catalog.searchByTitle')}
            value={searchInput}
            onChange={setSearchInput}
            onClear={() => setSearchInput('')}
            size="small"
          />

          <div className={styles.filterSort}>
            <BiSortAlt2 size={16} className={styles.filterSortIcon} />
            <select
              value={filters.sort || 'name'}
              onChange={(e) => setFilters({ sort: e.target.value })}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className={styles.newCategoryBtn}
            onClick={() => setIsVisibleModalNewCategory(true)}
          >
            <BiPlus size={18} />
            {t('catalog.newCategory')}
          </button>
        </div>

        <div className={styles.filterDividerLine} />

        <div className={styles.filterAdvancedRow}>
          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>{t('catalogFilter.level')}</span>
            <button
              type="button"
              className={classNames(styles.levelChip, { [styles.active]: !filters.level })}
              onClick={() => setFilters({ level: null })}
            >
              {t('common.all')}
            </button>
            {LEVELS_OPTIONS.map((l) => (
              <button
                key={l}
                type="button"
                className={classNames(styles.levelChip, styles[`level${l}`], { [styles.active]: filters.level === l })}
                onClick={() => setFilters({ level: filters.level === l ? null : l })}
              >
                {l}
              </button>
            ))}
          </div>

          <div className={styles.filterGroupSep} />

          <div className={styles.filterGroup}>
            {[
              { value: 'all', label: t('common.all') },
              { value: 'premium', label: `✦ ${t('common.premium')}` },
              { value: 'free', label: `◇ ${t('common.free')}` },
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={classNames(styles.badgeChip, styles[`premiumChip_${value}`], {
                  [styles.active]: filters.premiumFilter === value,
                })}
                onClick={() => setFilters({ premiumFilter: value })}
              >
                {label}
              </button>
            ))}
            <button
              type="button"
              className={classNames(styles.badgeChip, styles.newChip, { [styles.active]: filters.newContent })}
              onClick={() => setFilters({ newContent: !filters.newContent })}
            >
              🆕 {t('common.new')}
            </button>
          </div>

          <div className={styles.filterGroupSep} />

          <div className={styles.filterGroup}>
            <span className={styles.filterGroupLabel}>{t('catalogFilter.state')}</span>
            {[
              { value: 'all', label: t('common.all') },
              { value: 'active', label: `✅ ${t('common.active')}` },
              { value: 'inactive', label: `🚫 ${t('common.inactive')}` },
            ].map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={classNames(styles.badgeChip, styles[`availableChip_${value}`], {
                  [styles.active]: filters.available === value,
                })}
                onClick={() => setFilters({ available: value })}
              >
                {label}
              </button>
            ))}
          </div>

          {hasActiveFilters && (
            <button
              type="button"
              className={styles.clearAllBtn}
              onClick={resetFilters}
            >
              <FiX size={12} />
              {t('common.clearAll')}
              <span className={styles.clearAllCount}>{activeFilterCount}</span>
            </button>
          )}
        </div>
      </div>

      {/* ── New Content section ── */}
      <div className={styles.newContentSection}>
        <div className={styles.newContentTitle}>{t('catalogFilter.newContent')}</div>
        <div className={styles.newContentScroll}>
          {newContentItems.map((item) => (
            <div
              key={item.catalogRef || item.sourceId}
              className={styles.itemCard}
              role="button"
              tabIndex={0}
              onClick={() => {
                const path = item.type === 'deck' ? `/deck/edit/${item.sourceId}` : `/texts/edit/${item.sourceId}`;
                saveScrollAndNavigate(path);
              }}
            >
              <div className={styles.itemCardImageWrapper}>
                <img
                  src={getImageSrc(item)}
                  alt=""
                  className={styles.imgObjectFit}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = DEFAULT_DECK_AVATAR;
                  }}
                />
              </div>
              <div className={styles.itemCardBody}>
                <div className={styles.itemCardTitle}>{item.title}</div>
                <div className={styles.itemCardMeta}>
                  {item.level && (
                    <span className={classNames(styles.levelBadge, LEVEL_BADGE_CLASS[item.level])}>
                      {item.level}
                    </span>
                  )}
                  {item.contentCount != null && <span style={{ color: '#6F767E', fontSize: 11 }}>{item.contentCount} words</span>}
                </div>
                <div className={styles.itemBadges}>
                  {filters.type === 'all' && (
                    <span className={styles.typeLabel}>{item.type === 'deck' ? t('common.deck') : t('common.text')}</span>
                  )}
                  {item.premium && <span className={styles.premium}>{t('common.premium')}</span>}
                  {item.isNewContent && <span className={styles.new}>{t('common.new')}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Category list with DnD (paginated, scroll-to-load-more) ── */}
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={!hasFilterOtherThanType ? visibleCategories.map((c) => c.categoryId) : []}
          strategy={verticalListSortingStrategy}
        >
        <div
          className={styles.listCategories}
          onClick={(e) => {
            if (!e.target.closest('[data-catalog-item]')) clearSelection();
          }}
        >
          {loadingCategoryTree && catalogCategoryGroups.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: '#888' }}>
              {t('catalog.loadingCategories')}
            </div>
          )}
          {(() => {
            let nextFlatIndex = 0;
            return visibleCategories.map((cat) => {
              const catId = cat.categoryId;
              const isExpanded = expandedCategoryIds.has(catId);
              const canReorder = !hasFilterOtherThanType;

              // Use full category from /admin/category (translations, emoji) when available for edit
              const fullCat = getFullCategoryById(categoriesFromApi, catId);
              const catProxy = fullCat || {
                _id: catId,
                title: cat.categoryTitle,
                emoji: cat.categoryEmoji ?? cat.emoji,
                translations: [],
              };

              const itemsInCategory = (cat.subcategories || []).reduce((n, sub) => n + (sub.items || []).length, 0);
              if (!isExpanded) nextFlatIndex += itemsInCategory;

              return (
                <SortableCategoryBlock key={catId} catId={catId} canReorder={canReorder}>
                  {(attributes, listeners, isDragging) => (
                <CategoryDroppable categoryId={catId}>
                <div className={styles.deckWrapper}>
                  <div className={styles.categoryHeader} onClick={() => toggleCategory(catProxy)}>
                    <div
                      className={styles.categoryTitle}
                      {...(canReorder ? { ...listeners, ...attributes, style: { cursor: isDragging ? 'grabbing' : 'grab' } } : {})}
                    >
                      {canReorder && (
                        <span className={classNames(styles.subcategoryDragHandle, isDragging && styles.dragging)} title="Drag to reorder">
                          <MdDragIndicator size={18} />
                        </span>
                      )}
                      {(catProxy?.emoji || cat?.categoryEmoji || cat?.emoji) && (
                        <span className={styles.emoji}>{catProxy?.emoji || cat?.categoryEmoji || cat?.emoji}</span>
                      )}
                      <span>{catProxy?.title || cat?.categoryTitle}</span>
                    </div>
                    <div className={styles.categoryActions} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className={styles.catBtnEdit}
                        onClick={() => handleShowEdit(catProxy)}
                      >
                        <FiEdit2 size={14} /> {t('common.edit')}
                      </button>
                      <button
                        type="button"
                        className={styles.catBtnAddSub}
                        onClick={() => handleShowNewSubcategory(catProxy)}
                      >
                        <BiPlus size={16} /> {t('catalog.subcategory')}
                      </button>
                      <button
                        type="button"
                        className={styles.catBtnDelete}
                        onClick={() => setDeleteCatModalState({ visible: true, target: catProxy })}
                        title={t('common.delete')}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <>
                      {(hasFilterOtherThanType
                        ? [...(cat.subcategories || [])].sort((a, b) => {
                            const aHas = (a.items || []).length > 0;
                            const bHas = (b.items || []).length > 0;
                            if (aHas === bHas) return 0;
                            return aHas ? -1 : 1;
                          })
                        : (cat.subcategories || [])
                      ).map((sub) => {
                        const isLimbo = !sub.subCategoryId;
                        const subId = sub.subCategoryId || '__limbo__';
                        const movedTo = subcategoryMovedLabel[sub.subCategoryId];
                        // Use full subcategory from /admin/category (translations) when available for edit.
                        // Title is derived from translations (see getTranslatedTitle) so an edited
                        // subcategory name refreshes immediately instead of showing the stale `title` field.
                        const fullSub = isLimbo ? null : getFullSubcategoryById(categoriesFromApi, catId, sub.subCategoryId);
                        const subProxy = isLimbo
                          ? { _id: 'limbo', title: t('catalog.noSubcategory'), emoji: null, parent: catId, translations: [] }
                          : (fullSub
                              ? { ...fullSub, title: getTranslatedTitle(fullSub.translations, userLang) || fullSub.title || sub.subCategoryTitle }
                              : {
                                  _id: sub.subCategoryId,
                                  title: sub.subCategoryTitle,
                                  emoji: sub.subCategoryEmoji ?? sub.emoji,
                                  parent: catId,
                                  translations: [],
                                });

                        const subKey = `${catId}:${subId}`;
                        const isSubExpanded = expandedSubcategoryIds.has(subKey);
                        const subItems = sub.items || [];
                        const flatIndexOffset = nextFlatIndex;
                        nextFlatIndex += subItems.length;
                        // Use totalItems from API when available for accurate Load more visibility; fallback to heuristic
                        const paginationState = subcategoryPagination[subKey];
                        const hasMoreFromApi = typeof sub.totalItems === 'number' && sub.totalItems > subItems.length;
                        const hasMoreFromPagination = paginationState?.hasMore;
                        const hasMoreHeuristic = subItems.length === ITEMS_PER_SUBCATEGORY_PAGE;
                        const hasMoreItems = !isLimbo && (hasMoreFromPagination ?? hasMoreFromApi ?? hasMoreHeuristic);
                        return (
                          <SubcategoryRow
                            key={subId}
                            subcategory={subProxy}
                            items={subItems}
                            categoryId={catId}
                            subcategoryId={subId}
                            isExpanded={isSubExpanded}
                            onToggle={() => toggleSubcategory(catId, subId)}
                            movedToLabel={movedTo}
                            filterType={filters.type}
                            hasMoreItems={hasMoreItems}
                            loadingMore={!isLimbo && !!subcategoryPagination[`${catId}:${subId}`]?.loading}
                            onLoadMore={!isLimbo ? () => loadMoreSubcategoryItems(catId, subId) : undefined}
                            onEdit={isLimbo ? undefined : () => handleShowEdit(subProxy)}
                            onAddDeck={isLimbo ? undefined : () => {
                              const categoryId = subProxy.parent || catId;
                              const subcategoryId = subProxy._id;
                              saveScrollAndNavigate('/deck/add', { fromCatalog: true, categoryId, subcategoryId });
                            }}
                            onAddText={isLimbo ? undefined : () => {
                              const categoryId = subProxy.parent || catId;
                              const subcategoryId = subProxy._id;
                              saveScrollAndNavigate('/texts/add', { fromCatalog: true, categoryId, subcategoryId });
                            }}
                            onDeleteCategory={isLimbo ? undefined : () => setDeleteCatModalState({ visible: true, target: subProxy })}
                            onDeleteDeck={(deck) => {
                              setCategoryIdSelected(catProxy);
                              setDeckSelected(deck);
                              setIsVisibleModalDeleteDeck(true);
                            }}
                            getImageSrc={getImageSrc}
                            onOpenItem={saveScrollAndNavigate}
                            onOpenStats={(item) => setStatsModalState({ visible: true, item })}
                            onOpenDelete={(item) => setDeleteModalState({ visible: true, item })}
                            selectedItemIds={selectedItemIds}
                            onItemClick={handleItemClick}
                            flatIndexOffset={flatIndexOffset}
                          />
                        );
                      })}
                    </>
                  )}
                </div>
              </CategoryDroppable>
                  )}
                </SortableCategoryBlock>
              );
            });
          })()}
        </div>
        </SortableContext>

        {/* Scroll sentinel: load next 5 categories when this enters viewport */}
        {(hasMoreCategories || loadingMoreCategories) && (
          <div ref={sentinelRef} className={styles.loadMoreSentinel}>
            <span className={styles.loadMoreLabel}>
              {loadingMoreCategories ? t('catalog.loadingCategories') : t('catalog.scrollLoadMore')}
            </span>
          </div>
        )}

        <DragOverlay dropAnimation={null}>
          {activeDragItem && (
            <div className={classNames(styles.deckWrapperWW, styles.dragOverlayCard)} style={{ width: 230 }}>
              {activeDragItems.length > 1 && (
                <div className={styles.dragOverlayBadge}>{activeDragItems.length}</div>
              )}
              <div className={styles.imgWrapperWW}>
                <img
                  src={getImageSrc(activeDragItem)}
                  className={styles.imgObjectFit}
                  alt=""
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = DEFAULT_DECK_AVATAR;
                  }}
                />
                <div className={styles.cardBadgeRow}>
                  {filters.type === 'all' && (
                    <span className={styles.cardBadgeType}>{activeDragItem.type === 'deck' ? t('common.deck') : t('common.text')}</span>
                  )}
                  {activeDragItem.premium && <span className={styles.cardBadgePremium}>Premium</span>}
                </div>
              </div>
              <div className={styles.deckInfoWW}>
                <div className={styles.cardInfoTitle}>
                  {activeDragItems.length > 1
                    ? (t('catalog.draggingItems', { count: activeDragItems.length }) || `${activeDragItems.length} items`)
                    : activeDragItem.title}
                </div>
                <div className={styles.cardInfoMeta}>
                  {activeDragItem.level && (
                    <span className={classNames(styles.levelBadge, LEVEL_BADGE_CLASS[activeDragItem.level])}>
                      {activeDragItem.level}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          {activeDragSubcategory && (
            <div className={styles.subcategoryDragOverlay}>
              <MdDragIndicator size={20} />
              {activeDragSubcategory.emoji && <span>{activeDragSubcategory.emoji}</span>} {activeDragSubcategory.title}
            </div>
          )}
          {activeDragCategory && (
            <div className={classNames(styles.deckWrapper, styles.categoryDragOverlay)}>
              <div className={styles.categoryHeader}>
                <div className={styles.categoryTitle}>
                  <span className={styles.subcategoryDragHandle}>
                    <MdDragIndicator size={18} />
                  </span>
                  {activeDragCategory.emoji && <span className={styles.emoji}>{activeDragCategory.emoji}</span>}
                  <span>{activeDragCategory.categoryTitle}</span>
                </div>
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <div className={styles.contentSpacer} />

      <CatalogStatsModal
        visible={statsModalState.visible}
        onClose={() => setStatsModalState({ visible: false, item: null })}
        item={statsModalState.item}
      />

      <Modal
        visible={deleteModalState.visible}
        onClose={() => setDeleteModalState({ visible: false, item: null })}
      >
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <h3 style={{ marginBottom: '20px' }}>{t('common.confirmDelete')}</h3>
          <p style={{ marginBottom: '12px', fontSize: '16px' }}>
            {t('catalog.deleteConfirmPrompt')}
          </p>
          <p style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600, wordBreak: 'break-word' }}>
            "{getItemDisplayTitle(deleteModalState.item) || t('common.untitled')}"
          </p>
          <div style={{ marginBottom: '24px', fontSize: '16px' }}>
            <TextInput
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
              placeholder={t('catalog.typeToConfirmDelete')}
              className={styles.deleteConfirmInput}
            />
          </div>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button
              className="button-stroke"
              onClick={() => setDeleteModalState({ visible: false, item: null })}
              disabled={isDeletingItem}
            >
              {t('common.cancel')}
            </button>
            <button
              className="button"
              onClick={handleDeleteConfirm}
              disabled={isDeletingItem || deleteConfirmInput.trim() !== (getItemDisplayTitle(deleteModalState.item) || t('common.untitled'))}
              style={{ background: 'red', color: 'white', border: 'none' }}
            >
              {isDeletingItem ? `${t('common.deleting')}...` : t('common.delete')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        visible={deleteCatModalState.visible}
        onClose={() => setDeleteCatModalState({ visible: false, target: null })}
      >
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <h3 style={{ marginBottom: '20px' }}>
            {deleteCatModalState.target?.parent
              ? t('catalog.deleteSubcategoryTitle') || 'Eliminar subcategoría'
              : t('catalog.deleteCategoryTitle') || 'Eliminar categoría'}
          </h3>
          <p style={{ marginBottom: '12px', fontSize: '16px' }}>
            {deleteCatModalState.target?.parent
              ? t('catalog.deleteSubcategoryPrompt') || '¿Estás seguro de que deseas eliminar esta subcategoría?'
              : t('catalog.deleteCategoryPrompt') || '¿Estás seguro de que deseas eliminar esta categoría?'}
          </p>
          <p style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600, wordBreak: 'break-word' }}>
            {deleteCatModalState.target?.emoji && <span>{deleteCatModalState.target.emoji} </span>}
            "{deleteCatModalState.target?.title || t('common.untitled')}"
          </p>
          <p style={{ marginBottom: '20px', fontSize: '13px', color: '#6F767E' }}>
            {deleteCatModalState.target?.parent
              ? t('catalog.deleteSubcategoryNote') || 'Solo se puede eliminar si no tiene items (decks/textos) asignados.'
              : t('catalog.deleteCategoryNote') || 'Solo se puede eliminar si no tiene subcategorías.'}
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button
              className="button-stroke"
              onClick={() => setDeleteCatModalState({ visible: false, target: null })}
              disabled={isDeletingCat}
            >
              {t('common.cancel')}
            </button>
            <button
              className="button"
              onClick={handleDeleteCategoryConfirm}
              disabled={isDeletingCat}
              style={{ background: '#e44', color: 'white', border: 'none' }}
            >
              {isDeletingCat ? `${t('common.deleting') || 'Eliminando'}...` : t('common.delete') || 'Eliminar'}
            </button>
          </div>
        </div>
      </Modal>

      </Card>
    </div>
  );
}

function SortableCategoryBlock({
  catId,
  canReorder,
  children,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: catId,
    data: { type: 'category', categoryId: catId },
  });

  return (
    <div
      ref={setNodeRef}
      className={isDragging ? styles.categoryDragPlaceholder : undefined}
      style={{
        opacity: isDragging ? 0.4 : 1,
        pointerEvents: isDragging ? 'none' : undefined,
        transition,
      }}
    >
      {children(attributes, listeners, isDragging)}
    </div>
  );
}

function CategoryDroppable({ categoryId, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `category:${categoryId}`,
    data: { type: 'category', categoryId },
  });
  return (
    <div ref={setNodeRef} className={classNames(isOver && styles.dropZone, isOver && styles.over)}>
      {children}
    </div>
  );
}

function SubcategoryRow({
  subcategory,
  items,
  categoryId,
  subcategoryId,
  isExpanded,
  onToggle,
  movedToLabel,
  filterType,
  hasMoreItems,
  loadingMore,
  onLoadMore,
  onEdit,
  onAddDeck,
  onAddText,
  onDeleteDeck,
  onDeleteCategory,
  getImageSrc,
  onOpenItem,
  onOpenStats,
  onOpenDelete,
  selectedItemIds,
  onItemClick,
  flatIndexOffset,
}) {
  const { t } = useTranslation();
  const subId = (subcategory._id || subcategory).toString();
  const isLimbo = subId === 'limbo';
  const isEmpty = !items || items.length === 0;
  const hasItems = !isEmpty;
  const showItems = hasItems && isExpanded;

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `subcategory:${subId}:${categoryId}`,
    data: { type: 'subcategory', subcategoryId: subId, categoryId },
  });

  // Expand collapsed empty subcategory when dragging item over it
  const prevIsOverRef = useRef(false);
  useEffect(() => {
    if (isEmpty && !isExpanded && isOver && !prevIsOverRef.current) {
      onToggle();
    }
    prevIsOverRef.current = isOver;
  }, [isEmpty, isExpanded, isOver, onToggle]);

  const handleHeaderClick = (hasItems || isEmpty) ? onToggle : undefined;

  // When empty and collapsed: whole block is drop zone. When empty and expanded: inner div is drop zone. When hasItems and collapsed: block is drop zone.
  const blockRef = (hasItems && !isExpanded) || (isEmpty && !isExpanded) ? setDropRef : undefined;
  return (
    <div
      ref={blockRef}
      className={classNames(
        styles.subcategoryBlock,
        isEmpty && !isExpanded && styles.subcategoryCollapsed,
        ((hasItems || isEmpty) && !isExpanded && styles.dropZone),
        ((hasItems || isEmpty) && !isExpanded && isOver && styles.over)
      )}
    >
      <div
        className={classNames(styles.subcategoryHeader, (hasItems || isEmpty) && styles.subcategoryHeaderClickable)}
        onClick={handleHeaderClick}
      >
        <div className={styles.subcategoryTitle}>
          {!isLimbo && (
            <SubcategoryDragHandle subcategory={subcategory} />
          )}
          {(subcategory?.emoji) && (
            <span className={styles.emoji}>{subcategory.emoji}</span>
          )}
          <span>{subcategory?.title}</span>
          {movedToLabel && (
            <span className={styles.subcategoryMovedLabel}>{t('catalog.movedTo', { label: movedToLabel })}</span>
          )}
          {(hasItems || isEmpty) && (
            <FiChevronDown size={14} className={styles.subcategoryChevron} style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }} />
          )}
        </div>
        <div className={styles.categoryActions} onClick={(e) => e.stopPropagation()}>
          {onEdit && (
            <button type="button" className={styles.catBtnEdit} onClick={onEdit}>
              <FiEdit2 size={13} /> {t('common.edit')}
            </button>
          )}
          {onAddDeck && (
            <AddContentButton
              filterType={filterType}
              onAddDeck={onAddDeck}
              onAddText={onAddText}
              small
            />
          )}
          {onDeleteCategory && (
            <button
              type="button"
              className={styles.catBtnDelete}
              onClick={onDeleteCategory}
              title={t('common.delete')}
            >
              <FiTrash2 size={13} />
            </button>
          )}
        </div>
      </div>
      {isEmpty && isExpanded && (
        <div
          ref={setDropRef}
          className={classNames(styles.emptySubcategoryDrop, styles.dropZone, isOver && styles.over)}
        >
          <span className={styles.emptySubcategoryText}>{t('catalog.noItems')}</span>
        </div>
      )}
      {showItems && (
        <>
          <div
            ref={setDropRef}
            className={classNames(styles.itemsRow, styles.dropZone, isOver && styles.over)}
          >
            {items.map((item, idx) => (
              <CatalogItemCard
                key={item.catalogRef || item.sourceId}
                item={item}
                getImageSrc={getImageSrc}
                filterType={filterType}
                isSelected={selectedItemIds?.has(getItemId(item))}
                flatIndex={flatIndexOffset + idx}
                onItemClick={onItemClick}
                onOpen={onOpenItem ? () => {
                  const path = item.type === 'deck' ? `/deck/edit/${item.sourceId}` : `/texts/edit/${item.sourceId}`;
                  onOpenItem(path);
                } : undefined}
                onDelete={item.type === 'deck' && onDeleteDeck ? () => onDeleteDeck(item) : undefined}
                onOpenItem={onOpenItem}
                onOpenStats={onOpenStats}
                onOpenDelete={onOpenDelete}
              />
            ))}
          </div>
          {hasMoreItems && onLoadMore && (
            <div className={styles.loadMoreSubcategory}>
              <button
                type="button"
                className={styles.loadMoreSubcategoryBtn}
                onClick={onLoadMore}
                disabled={loadingMore}
              >
                <BiPlus size={18} />
                {loadingMore ? t('common.loading') : t('catalog.loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SubcategoryDragHandle({ subcategory }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `subcategory-drag:${(subcategory._id || subcategory).toString()}`,
    data: { type: 'subcategory', subcategory },
  });

  return (
    <span
      ref={setNodeRef}
      className={classNames(styles.subcategoryDragHandle, isDragging && styles.dragging)}
      {...listeners}
      {...attributes}
    >
      <MdDragIndicator size={18} />
    </span>
  );
}

function CatalogItemCard({ item, getImageSrc, filterType, isSelected, flatIndex, onItemClick, onOpen, onDelete, onOpenItem, onOpenStats, onOpenDelete }) {
  const { t } = useTranslation();
  const id = item.catalogRef || `item:${item.type}:${item.sourceId}`;
  const history = useHistory();
  const navigate = onOpenItem || ((path) => history.push(path));
  const [imagesModalOpen, setImagesModalOpen] = useState(false);
  const [deckImages, setDeckImages] = useState(null);
  const [loadingImages, setLoadingImages] = useState(false);
  const [dragCollapsed, setDragCollapsed] = useState(false);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { type: 'item', item },
  });

  useEffect(() => {
    if (!isDragging) setDragCollapsed(false);
  }, [isDragging]);

  const handleDownloadCSV = async (e) => {
    e.stopPropagation();
    try {
      const payload = { _id: item.sourceId, key: item.key };
      const data = await getDeckCSV(payload);
      if (data?.data) {
        const blob = new Blob([`\ufeff${data.data}`], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${item?.key || item?.title || 'deck'}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        toast.error(data?.response?.data?.message || t('toast.failedToDownloadCsv'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || t('toast.failedToDownloadCsv'));
    }
  };

  const handleViewImages = async (e) => {
    e.stopPropagation();
    setImagesModalOpen(true);
    setLoadingImages(true);
    setDeckImages(null);
    try {
      const fetcher = item.type === 'text' ? getTextImages : getDeckImages;
      const res = await fetcher({ _id: item.sourceId });
      const data = res?.data ?? res;
      const isError = res?.response ?? (res?.status >= 400);
      const imagesData = isError ? {} : (typeof data === 'object' && data !== null ? data : {});
      setDeckImages(imagesData);
      if (isError) toast.error(res?.response?.data?.message || t('toast.errorOccurred'));
    } catch (err) {
      setDeckImages({});
      toast.error(err?.response?.data?.message || t('toast.errorOccurred'));
    } finally {
      setLoadingImages(false);
    }
  };

  const actions = [];
  if (item.type === 'deck') {
    actions.push(
      { title: t('common.viewStats'), icon: 'bar-chart', action: (e) => { e.stopPropagation(); onOpenStats?.(item); } },
      { title: t('common.downloadCsv'), icon: 'download', action: handleDownloadCSV },
      { title: t('common.viewImages'), icon: 'photos', action: handleViewImages },
      { title: t('common.delete'), icon: 'trash', danger: true, action: (e) => { e.stopPropagation(); onOpenDelete?.(item); } }
    );
  } else if (item.type === 'text') {
    actions.push(
      { title: t('common.viewStats'), icon: 'bar-chart', action: (e) => { e.stopPropagation(); onOpenStats?.(item); } },
      { title: t('common.viewImages'), icon: 'photos', action: handleViewImages },
      { title: t('common.content'), icon: 'edit', action: (e) => { e.stopPropagation(); navigate(`/texts/content/${item.sourceId}`); } },
      { title: t('common.delete'), icon: 'trash', danger: true, action: (e) => { e.stopPropagation(); onOpenDelete?.(item); } }
    );
  }
  const selectionHandledRef = useRef(false);
  const handleSelectionClick = (e) => {
    if (isDragging || !onItemClick) return;
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      selectionHandledRef.current = true;
      e.stopPropagation();
      e.preventDefault();
      onItemClick(item, flatIndex, e);
    } else {
      selectionHandledRef.current = false;
    }
  };

  const handleCardClick = (e) => {
    if (isDragging) return;
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      if (selectionHandledRef.current) {
        selectionHandledRef.current = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      handleSelectionClick(e);
      return;
    }
    if (onItemClick) {
      onItemClick(item, flatIndex, e);
    } else {
      onOpen?.();
    }
  };

  const handleCardDoubleClick = () => {
    if (!isDragging && onOpen) onOpen();
  };

  return (
    <>
      {/* Wrapper externo para capturar shift/ctrl+click ANTES de que dnd-kit lo consuma */}
      <div
        ref={setNodeRef}
        data-catalog-item
        className={classNames(
          styles.deckWrapperWW,
          isDragging && styles.draggingHidden,
          isDragging && dragCollapsed && styles.draggingHiddenCollapsed,
          isSelected && styles.itemSelected
        )}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        onPointerDownCapture={handleSelectionClick}
        onClick={handleCardClick}
        onDoubleClick={handleCardDoubleClick}
        onTransitionEnd={(e) => {
          if (e.propertyName === 'opacity' && isDragging) setDragCollapsed(true);
        }}
        role="button"
        tabIndex={0}
      >
        <div
          className={styles.imgWrapperWW}
          {...listeners}
          {...attributes}
        >
          <img
            src={getImageSrc(item)}
            className={styles.imgObjectFit}
            alt=""
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = DEFAULT_DECK_AVATAR;
            }}
          />
          <div className={styles.cardBadgeRow}>
            {filterType === 'all' && (
              <span className={styles.cardBadgeType}>{item.type === 'deck' ? t('common.deck') : t('common.text')}</span>
            )}
            {item.premium && <span className={styles.cardBadgePremium}>{t('common.premium')}</span>}
            {item.isNewContent && <span className={styles.cardBadgeNew}>{t('common.new')}</span>}
            {!item.available && <span className={styles.cardBadgeInactive}>{t('common.inactive')}</span>}
          </div>
          {actions.length > 0 && (
            <div className={styles.cardOverlayChip} onClick={(e) => e.stopPropagation()}>
              {item.type === 'text' && (
                <button
                  type="button"
                  className={styles.cardOverlayChipBtn}
                  onClick={(e) => { e.stopPropagation(); navigate(`/texts/content/${item.sourceId}`); }}
                  title={t('common.content')}
                >
                  <FiFileText size={14} />
                </button>
              )}
              <Actions
                className={styles.cardOverlayActions}
                classActionsHead={styles.cardOverlayActionsHead}
                items={actions}
                small
                portal
              />
            </div>
          )}
        </div>
        <div className={styles.deckInfoWW}>
          <div className={styles.cardInfoTitle}>{item.title}</div>
          <div className={styles.cardInfoMeta}>
            {item.level && (
              <span className={classNames(styles.levelBadge, LEVEL_BADGE_CLASS[item.level])}>
                {item.level}
              </span>
            )}
            {item.contentCount != null && (
              <span className={styles.cardMetaWords}>{item.contentCount} words</span>
            )}
          </div>
        </div>
      </div>
      <Modal
        visible={imagesModalOpen}
        onClose={() => setImagesModalOpen(false)}
        outerClassName={styles.imagesModalOuter}
      >
        <div className={styles.imagesModalContent}>
          <div className={styles.imagesModalTitle}>
            {t('common.viewImages')} — {item.title}
          </div>
          {loadingImages ? (
            <div className={styles.imagesModalLoading}>{t('common.loading')}...</div>
          ) : deckImages ? (
            <div className={styles.imagesGrid}>
              {['micro', 'mini', 'small', 'medium', 'normal', 'default'].map((size) =>
                deckImages[size] ? (
                  <div key={size} className={styles.imageItem}>
                    <div className={styles.imageLabel}>{size}</div>
                    <img
                      src={deckImages[size]}
                      alt={`${item.title} ${size}`}
                      className={styles.imagePreview}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = DEFAULT_DECK_AVATAR;
                      }}
                    />
                    <a
                      href={deckImages[size]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.imageLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      URL ↗
                    </a>
                  </div>
                ) : null
              )}
              {Object.values(deckImages).every((v) => !v) && (
                <div className={styles.imagesModalLoading}>
                  {t('decks.deckImage')} — N/A
                </div>
              )}
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}

function AddContentButton({ filterType, onAddDeck, onAddText, small }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (filterType === 'deck') {
    return (
      <button type="button" className={styles.catBtnAdd} onClick={onAddDeck}>
        <BiPlus size={small ? 14 : 16} /> {t('common.addDeck')}
      </button>
    );
  }

  if (filterType === 'text') {
    return (
      <button type="button" className={styles.catBtnAdd} onClick={onAddText}>
        <BiPlus size={small ? 14 : 16} /> {t('common.addText')}
      </button>
    );
  }

  return (
    <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
      <div className={styles.addDropdownWrap}>
        <button
          type="button"
          className={styles.catBtnAdd}
          onClick={() => setOpen(!open)}
        >
          <BiPlus size={small ? 14 : 16} /> Add
          <FiChevronDown size={14} style={{ marginLeft: 2, transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }} />
        </button>
        {open && (
          <div className={styles.addDropdownMenu}>
            <button
              type="button"
              className={styles.addDropdownItem}
              onClick={() => { setOpen(false); onAddDeck?.(); }}
            >
              🃏 {t('common.addDeck')}
            </button>
            <button
              type="button"
              className={styles.addDropdownItem}
              onClick={() => { setOpen(false); onAddText?.(); }}
            >
              📄 {t('common.addText')}
            </button>
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
}
